import { useEffect, useState, useCallback } from 'react';
import grapesjs from 'grapesjs';
import 'grapesjs/dist/css/grapes.min.css';
import 'grapesjs-preset-webpage';
import 'grapesjs-plugin-forms';

// 導入編輯器組件
import React from 'react';
import ReactDOM from 'react-dom/client'; // 使用新版本的 ReactDOM
import {
  TextInputEditor,
  TextAreaEditor,
  SelectEditor,
  RadioEditor,
  CheckboxEditor,
  ButtonEditor,
  LabelEditor,
  ImageEditor
} from '../components/EFormDesigner/editors';

// 動態載入語言資源
const loadLanguageResources = async (language) => {
  try {
    let resources;
    switch (language) {
      case 'zh-SC':
        resources = await import('../locales/zh-SC.js');
        return resources.default;
      case 'zh-TC':
        resources = await import('../locales/zh-TC.js');
        return resources.default;
      case 'en':
        resources = await import('../locales/en.js');
        return resources.default;
      default:
        resources = await import('../locales/zh-TC.js');
        return resources.default;
    }
  } catch (error) {
    console.error('載入語言資源失敗:', error);
    // 返回默認的繁體中文
    return {
      eformDesigner: {
        welcomeMessage: '歡迎使用表單設計器',
        welcomeDescription: '請從左側面板拖拽組件到此處開始設計您的表單。',
        exampleInput: '示例輸入框',
        exampleInputPlaceholder: '請輸入...'
      }
    };
  }
};

const useGrapesJS = (containerRef, initialHtmlContent, onEditorReady) => {
  const [editor, setEditor] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [languageResources, setLanguageResources] = useState(null);

  console.log('🚀 useGrapesJS: Hook 初始化，參數:', {
    hasContainerRef: !!containerRef,
    hasInitialHtmlContent: !!initialHtmlContent,
    hasOnEditorReady: !!onEditorReady,
    onEditorReadyType: onEditorReady ? typeof onEditorReady : 'null'
  });

  useEffect(() => {
    console.log('🔍 useGrapesJS: useEffect 觸發，條件檢查:', {
      hasContainerRef: !!containerRef.current,
      hasEditor: !!editor,
      containerRefCurrent: containerRef.current
    });
    
    if (!containerRef.current || editor) {
      console.log('⚠️ useGrapesJS: 條件不滿足，退出初始化');
      return;
    }

    console.log('🚀 開始初始化 GrapesJS 編輯器...');
    
    // 獲取用戶語言設置
    const userLanguage = localStorage.getItem('language') || 'zh-TC';
    console.log('🌐 用戶語言:', userLanguage);
    
    // 載入語言資源並初始化編輯器
    loadLanguageResources(userLanguage).then(resources => {
      console.log('✅ 語言資源載入成功:', resources);
      setLanguageResources(resources);
      
      try {
        console.log('🔧 開始創建 GrapesJS 實例...');
        
        // 先創建一個臨時變量來存儲編輯器引用
        let editorRef = null;
        
        const grapesEditor = grapesjs.init({
          container: containerRef.current,
          height: '100%',
          width: 'auto',
          storageManager: false,
          plugins: ['gjs-preset-webpage'],
          pluginsOpts: {
            'gjs-preset-webpage': {}
          },
          canvas: {
            styles: [
              'https://stackpath.bootstrapcdn.com/bootstrap/4.1.3/css/bootstrap.min.css',
            ],
            scripts: [
              'https://stackpath.bootstrapcdn.com/bootstrap/4.1.3/js/bootstrap.min.js',
            ],
          },
          // 設備管理器配置
          deviceManager: {
            devices: [
              {
                name: resources.eformDesigner?.grapesJs?.desktop || 'Desktop',
                width: '',
              },
              {
                name: resources.eformDesigner?.grapesJs?.tablet || 'Tablet',
                width: '768px',
                widthMedia: '992px',
              },
              {
                name: resources.eformDesigner?.grapesJs?.mobileLandscape || 'Mobile Landscape',
                width: '568px',
                widthMedia: '768px',
              },
              {
                name: resources.eformDesigner?.grapesJs?.mobilePortrait || 'Mobile Portrait',
                width: '320px',
                widthMedia: '480px',
              },
            ],
          },
          // 資源管理器配置 - 自定義圖片上傳
          assetManager: {
            upload: false, // 禁用默認上傳
            embedAsBase64: false, // 禁用 base64 嵌入
            uploadFile: (ev) => {
              return new Promise((resolve, reject) => {
                const files = ev.dataTransfer ? ev.dataTransfer.files : ev.target.files;
                
                if (!files || files.length === 0) {
                  reject(new Error('沒有選擇文件'));
                  return;
                }

                const file = files[0];
                
                // 檢查文件類型
                const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];
                if (!allowedTypes.includes(file.type)) {
                  reject(new Error('不支持的文件類型，只允許圖片文件'));
                  return;
                }

                const formData = new FormData();
                formData.append('file', file);

                const token = localStorage.getItem('token');
                if (!token) {
                  reject(new Error('未找到認證令牌'));
                  return;
                }

                // 觸發上傳開始事件
                const editor = editorRef || grapesEditor;
                if (editor) {
                  editor.trigger('asset:upload:start', files);
                }

                // 上傳到後台
                fetch('/api/FormsUpload/image', {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${token}`
                  },
                  body: formData
                })
                .then(response => {
                  if (!response.ok) {
                    return response.text().then(text => {
                      throw new Error(`HTTP ${response.status}: ${text}`);
                    });
                  }
                  return response.json();
                })
                .then(result => {
                  if (result.success) {
                    // 構建圖片 URL
                    const imageUrl = `/Uploads/FormsFiles/Documents/${result.fileName}`;
                    
                    // 返回符合 grapesjs 格式的數據
                    // grapesjs 期望的格式: { data: [url1, url2, ...] } 或 { data: [{src: url1}, {src: url2}, ...] }
                    const assetData = {
                      data: [imageUrl]
                    };
                    
                    // 觸發上傳完成事件
                    if (editor) {
                      editor.trigger('asset:upload:end', assetData);
                    }
                    
                    resolve(assetData);
                  } else {
                    throw new Error(result.error || '上傳失敗');
                  }
                })
                .catch(error => {
                  console.error('❌ 圖片上傳錯誤:', error);
                  if (editor) {
                    editor.trigger('asset:upload:error', error);
                  }
                  reject(error);
                });
              });
            },
            autoAdd: true, // 自動添加上傳成功的資源
            showUrlInput: true, // 顯示 URL 輸入框
          },
          // 樣式管理器配置
          styleManager: {
            sectors: [
              {
                name: resources.eformDesigner?.grapesJs?.general || 'General',
                open: false,
                buildProps: [
                  'display',
                  'float',
                  'position',
                  'top',
                  'right',
                  'left',
                  'bottom',
                ],
                properties: [
                  {
                    property: 'display',
                    type: 'select',
                    defaults: 'block',
                    options: [
                      { value: 'block', name: resources.eformDesigner?.grapesJs?.block || 'Block' },
                      { value: 'inline', name: resources.eformDesigner?.grapesJs?.inline || 'Inline' },
                      { value: 'inline-block', name: resources.eformDesigner?.grapesJs?.inlineBlock || 'Inline Block' },
                      { value: 'flex', name: resources.eformDesigner?.grapesJs?.flex || 'Flex' },
                      { value: 'grid', name: resources.eformDesigner?.grapesJs?.grid || 'Grid' },
                    ],
                  },
                  {
                    property: 'float',
                    type: 'select',
                    defaults: 'none',
                    options: [
                      { value: 'none', name: resources.eformDesigner?.grapesJs?.none || 'None' },
                      { value: 'left', name: resources.eformDesigner?.grapesJs?.left || 'Left' },
                      { value: 'right', name: resources.eformDesigner?.grapesJs?.right || 'Right' },
                    ],
                  },
                  {
                    property: 'position',
                    type: 'select',
                    defaults: 'static',
                    options: [
                      { value: 'static', name: resources.eformDesigner?.grapesJs?.static || 'Static' },
                      { value: 'relative', name: resources.eformDesigner?.grapesJs?.relative || 'Relative' },
                      { value: 'absolute', name: resources.eformDesigner?.grapesJs?.absolute || 'Absolute' },
                      { value: 'fixed', name: resources.eformDesigner?.grapesJs?.fixed || 'Fixed' },
                    ],
                  },
                  {
                    property: 'top',
                    type: 'integer',
                    defaults: 'auto',
                    units: ['px', '%', 'em', 'rem'],
                  },
                  {
                    property: 'right',
                    type: 'integer',
                    defaults: 'auto',
                    units: ['px', '%', 'em', 'rem'],
                  },
                  {
                    property: 'left',
                    type: 'integer',
                    defaults: 'auto',
                    units: ['px', '%', 'em', 'rem'],
                  },
                  {
                    property: 'bottom',
                    type: 'integer',
                    defaults: 'auto',
                    units: ['px', '%', 'em', 'rem'],
                  },
                ],
              },
              {
                name: resources.eformDesigner?.grapesJs?.typography || 'Typography',
                open: true,
                buildProps: ['text-align', 'font-family', 'font-size', 'font-weight', 'font-style', 'text-decoration', 'color', 'line-height'],
                properties: [
                  {
                    property: 'text-align',
                    type: 'select',
                    defaults: 'left',
                    options: [
                      { value: 'left', name: resources.eformDesigner?.textAlignLeft || 'Left' },
                      { value: 'center', name: resources.eformDesigner?.textAlignCenter || 'Center' },
                      { value: 'right', name: resources.eformDesigner?.textAlignRight || 'Right' },
                      { value: 'justify', name: resources.eformDesigner?.textAlignJustify || 'Justify' },
                    ],
                  },
                  { property: 'font-family', type: 'text', defaults: '' },
                  { property: 'font-size', type: 'integer', defaults: '14', units: ['px', 'em', 'rem'] },
                  {
                    property: 'font-weight',
                    type: 'select',
                    defaults: 'normal',
                    options: [
                      { value: 'normal', name: resources.eformDesigner?.normal || 'Normal' },
                      { value: 'bold', name: resources.eformDesigner?.bold || 'Bold' },
                    ],
                  },
                  {
                    property: 'font-style',
                    type: 'select',
                    defaults: 'normal',
                    options: [
                      { value: 'normal', name: resources.eformDesigner?.normal || 'Normal' },
                      { value: 'italic', name: resources.eformDesigner?.italic || 'Italic' },
                    ],
                  },
                  {
                    property: 'text-decoration',
                    type: 'select',
                    defaults: 'none',
                    options: [
                      { value: 'none', name: resources.eformDesigner?.normal || 'None' },
                      { value: 'underline', name: resources.eformDesigner?.underline || 'Underline' },
                    ],
                  },
                  { property: 'color', type: 'color', defaults: '' },
                  { property: 'line-height', type: 'integer', defaults: '', units: ['', 'px', 'em'] },
                ],
              },
              // 邊框樣式（適用於 Table、TableCell 等線框設定）
              {
                name: resources.eformDesigner?.grapesJs?.border || 'Border',
                open: false,
                properties: [
                  {
                    property: 'border-width',
                    type: 'number',
                    defaults: '1',
                    units: ['px', 'em'],
                    min: 0,
                    label: resources.eformDesigner?.grapesJs?.borderWidth || 'Border Width',
                  },
                  {
                    property: 'border-style',
                    type: 'select',
                    defaults: 'solid',
                    options: [
                      { value: 'none', name: 'None' },
                      { value: 'solid', name: 'Solid' },
                      { value: 'dashed', name: 'Dashed' },
                      { value: 'dotted', name: 'Dotted' },
                      { value: 'double', name: 'Double' },
                    ],
                    label: resources.eformDesigner?.grapesJs?.borderStyle || 'Border Style',
                  },
                  {
                    property: 'border-color',
                    type: 'color',
                    defaults: '#000000',
                    label: resources.eformDesigner?.grapesJs?.borderColor || 'Border Color',
                  },
                  {
                    property: 'border-radius',
                    type: 'number',
                    defaults: '0',
                    units: ['px', '%', 'em'],
                    min: 0,
                    label: resources.eformDesigner?.grapesJs?.borderRadius || 'Border Radius',
                  },
                  {
                    property: 'border-collapse',
                    type: 'select',
                    defaults: 'collapse',
                    options: [
                      { value: 'separate', name: 'Separate' },
                      { value: 'collapse', name: 'Collapse' },
                    ],
                    label: resources.eformDesigner?.grapesJs?.borderCollapse || 'Border Collapse',
                  },
                ],
              },
            ],
          },
          // 新表單用預設外框；已存表單若有 <style> 則用存檔的 CSS/HTML，保留使用者自訂的 .container
          components: (() => {
            const defaultComponents = `
            <div class="container">
              <div class="row">
                <div class="col-md-12">
                  <h1>${resources.eformDesigner?.welcomeMessage || '歡迎使用表單設計器'}</h1>
                  <p>${resources.eformDesigner?.welcomeDescription || '請從左側面板拖拽組件到此處開始設計您的表單。'}</p>
                  <div class="form-group">
                    <label for="exampleInput">${resources.eformDesigner?.exampleInput || '示例輸入框'}</label>
                    <input type="text" class="form-control" id="exampleInput" placeholder="${resources.eformDesigner?.exampleInputPlaceholder || '請輸入...'}">
                  </div>
                </div>
              </div>
            </div>
          `;
            if (initialHtmlContent && typeof initialHtmlContent === 'string' && initialHtmlContent.includes('<style>')) {
              const styleMatch = initialHtmlContent.match(/<style[^>]*>([\s\S]*?)<\/style>/);
              if (styleMatch) {
                const savedHtml = initialHtmlContent.replace(/<style[^>]*>[\s\S]*?<\/style>/g, '').trim();
                return savedHtml || defaultComponents;
              }
            }
            return initialHtmlContent.trim() ? initialHtmlContent : defaultComponents;
          })(),
          style: (() => {
            const defaultStyle = `
            body { 
              font-family: Arial, sans-serif; 
              margin: 0; 
              padding: 20px; 
              background-color: #f5f5f5; 
            }
            .container { 
              max-width: 800px; 
              margin: 0 auto; 
              background: white; 
              padding: 20px; 
              border-radius: 8px; 
              box-shadow: 0 2px 10px rgba(0,0,0,0.1); 
            }
            
            /* Light Theme 自定義樣式 */
            .gjs-pn-views-container {
              background-color: #f8f9fa !important;
              border-color: #dee2e6 !important;
            }
            
            .gjs-pn-panel {
              background-color: #ffffff !important;
              border-color: #dee2e6 !important;
            }
            
            .gjs-pn-views {
              background-color: #f8f9fa !important;
            }
            
            .gjs-pn-btn {
              background-color: #ffffff !important;
              color: #495057 !important;
              border-color: #dee2e6 !important;
            }
            
            .gjs-pn-btn:hover {
              background-color: #e9ecef !important;
              color: #212529 !important;
            }
            
            .gjs-pn-btn.gjs-pn-active {
              background-color: #007bff !important;
              color: #ffffff !important;
            }
            
            .gjs-cv-canvas {
              background-color: #ffffff !important;
            }
            
            .gjs-block {
              background-color: #ffffff !important;
              border-color: #dee2e6 !important;
              color: #495057 !important;
            }
            
            .gjs-block:hover {
              background-color: #e9ecef !important;
            }
            
            .gjs-block-category {
              background-color: #f8f9fa !important;
              color: #495057 !important;
            }
            
            .gjs-layer-title {
              background-color: #ffffff !important;
              color: #495057 !important;
            }
            
            .gjs-layer-vis {
              background-color: #ffffff !important;
              color: #495057 !important;
            }
            
            .gjs-layer-caret {
              color: #495057 !important;
            }
            
            .gjs-property {
              background-color: #ffffff !important;
              color: #495057 !important;
            }
            
            .gjs-property input {
              background-color: #ffffff !important;
              color: #495057 !important;
              border-color: #dee2e6 !important;
            }
            
            .gjs-property select {
              background-color: #ffffff !important;
              color: #495057 !important;
              border-color: #dee2e6 !important;
            }
            
            .gjs-toolbar {
              background-color: #ffffff !important;
              border-color: #dee2e6 !important;
            }
            
            .gjs-toolbar-item {
              background-color: #ffffff !important;
              color: #495057 !important;
              border-color: #dee2e6 !important;
            }
            
            .gjs-toolbar-item:hover {
              background-color: #e9ecef !important;
            }
            
            .gjs-toolbar-item.gjs-active {
              background-color: #007bff !important;
              color: #ffffff !important;
            }
            
            /* input 和 textarea 的樣式 */
            .gjs-cv-canvas input,
            .gjs-cv-canvas textarea {
              background-color: #f8f9fa !important;
              color: #6c757d !important;
              border: 1px solid #dee2e6 !important;
              padding: 8px !important;
              font-size: 14px !important;
              line-height: 1.4 !important;
              opacity: 0.8 !important;
              cursor: text !important;
            }
            
            .gjs-cv-canvas input:hover,
            .gjs-cv-canvas textarea:hover {
              background-color: #e9ecef !important;
              border-color: #adb5bd !important;
            }
            
            /* 當組件被選中時，顯示提示樣式 */
            .gjs-selected input,
            .gjs-selected textarea {
              border: 2px solid #007bff !important;
              background-color: #f0f8ff !important;
            }
            
            /* 日期輸入框的格式提示 */
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"] {
              position: relative;
            }
            
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"]::before {
              content: "📅 格式: " attr(data-date-format);
              position: absolute;
              top: -25px;
              left: 0;
              background: rgba(0, 123, 255, 0.9);
              color: white;
              padding: 3px 8px;
              border-radius: 4px;
              font-size: 11px;
              white-space: nowrap;
              pointer-events: none;
              z-index: 1000;
              font-weight: 500;
              box-shadow: 0 2px 4px rgba(0,0,0,0.2);
              animation: fadeIn 0.3s ease-in-out;
              opacity: 0.9;
              transition: opacity 0.2s ease;
            }
            
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"]:hover::before {
              opacity: 1;
            }
            
            @keyframes fadeIn {
              from { opacity: 0; transform: translateY(-5px); }
              to { opacity: 0.9; transform: translateY(0); }
            }
            
            /* 當日期輸入框被選中時，顯示更詳細的格式信息 */
            .gjs-selected input[type="date"], .gjs-selected input[data-input-type="date"]::after {
              content: "📅 當前格式: " attr(data-date-format);
              position: absolute;
              top: -45px;
              left: 0;
              background: rgba(0, 123, 255, 1);
              color: white;
              padding: 6px 10px;
              border-radius: 6px;
              font-size: 12px;
              white-space: nowrap;
              pointer-events: none;
              z-index: 1001;
              font-weight: bold;
              box-shadow: 0 4px 8px rgba(0,0,0,0.3);
              border: 2px solid rgba(255,255,255,0.3);
              animation: slideIn 0.3s ease-in-out;
              opacity: 1;
              transition: all 0.2s ease;
            }
            
            .gjs-selected input[type="date"], .gjs-selected input[data-input-type="date"]:hover::after {
              transform: translateY(-2px) scale(1.05);
              box-shadow: 0 6px 12px rgba(0,0,0,0.4);
            }
            
            @keyframes slideIn {
              from { opacity: 0; transform: translateY(-10px); }
              to { opacity: 1; transform: translateY(0); }
            }
            
            /* 確保工具欄可以點擊 */
            .gjs-toolbar {
              pointer-events: auto !important;
              z-index: 1001 !important;
            }
            
            .gjs-toolbar-item {
              pointer-events: auto !important;
              cursor: pointer !important;
              z-index: 1002 !important;
            }
            
            /* 確保選中的組件容器可以接收點擊事件 */
            .gjs-selected {
              pointer-events: auto !important;
            }
            
            /* 日期輸入框的額外樣式 */
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"] {
              border: 2px solid #e8f4fd !important;
              background-color: #f8fbff !important;
              color: #1890ff !important;
              font-weight: 500 !important;
              position: relative !important;
              min-height: 32px !important;
              transition: all 0.2s ease !important;
              cursor: pointer !important;
            }
            
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"]:hover {
              border-color: #1890ff !important;
              background-color: #f0f8ff !important;
              transform: translateY(-1px) !important;
              box-shadow: 0 2px 8px rgba(24, 144, 255, 0.15) !important;
            }
            
            .gjs-selected input[type="date"], .gjs-selected input[data-input-type="date"] {
              border-color: #1890ff !important;
              background-color: #e6f7ff !important;
              box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2) !important;
              transform: scale(1.02) !important;
            }
            
            /* 日期輸入框的圖標樣式 */
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"]::-webkit-calendar-picker-indicator {
              background-color: #1890ff !important;
              border-radius: 3px !important;
              padding: 2px !important;
              cursor: pointer !important;
              transition: all 0.2s ease !important;
              filter: drop-shadow(0 1px 2px rgba(0,0,0,0.1)) !important;
            }
            
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"]::-webkit-calendar-picker-indicator:hover {
              background-color: #40a9ff !important;
              transform: scale(1.1) !important;
              filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2)) !important;
            }
            
            /* 日期輸入框的圖標樣式 - Firefox */
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"]::-moz-calendar-picker-indicator {
              background-color: #1890ff !important;
              border-radius: 3px !important;
              padding: 2px !important;
              cursor: pointer !important;
              transition: all 0.2s ease !important;
              filter: drop-shadow(0 1px 2px rgba(0,0,0,0.1)) !important;
            }
            
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"]::-moz-calendar-picker-indicator:hover {
              background-color: #40a9ff !important;
              transform: scale(1.1) !important;
              filter: drop-shadow(0 2px 4px rgba(0,0,0,0.2)) !important;
            }
            
            /* 日期輸入框的圖標樣式 - Edge */
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"]::-ms-clear,
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"]::-ms-expand {
              background-color: #1890ff !important;
              border-radius: 3px !important;
              padding: 2px !important;
              cursor: pointer !important;
              transition: all 0.2s ease !important;
            }
            
            /* 日期輸入框的響應式樣式 */
            @media (max-width: 768px) {
              .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"]::before {
                font-size: 10px !important;
                padding: 2px 6px !important;
                top: -20px !important;
              }
              
              .gjs-selected input[type="date"], .gjs-selected input[data-input-type="date"]::after {
                font-size: 11px !important;
                padding: 4px 8px !important;
                top: -35px !important;
              }
            }
            
            /* 日期輸入框的打印樣式 */
            @media print {
              .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"]::before,
              .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"]::after {
                display: none !important;
              }
            }
            
            /* 日期輸入框的深色主題支持 */
            @media (prefers-color-scheme: dark) {
              .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"] {
                background-color: #1f1f1f !important;
                color: #ffffff !important;
                border-color: #404040 !important;
              }
              
              .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"]:hover {
                background-color: #2a2a2a !important;
                border-color: #1890ff !important;
              }
              
              .gjs-selected input[type="date"], .gjs-selected input[data-input-type="date"] {
                background-color: #0d1419 !important;
                border-color: #1890ff !important;
              }
            }
            
            /* 日期輸入框的高對比度模式支持 */
            @media (prefers-contrast: high) {
              .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"] {
                border-width: 3px !important;
                border-color: #000000 !important;
                background-color: #ffffff !important;
                color: #000000 !important;
              }
              
              .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"]:hover {
                border-color: #1890ff !important;
                background-color: #f0f8ff !important;
              }
            }
            
            /* 日期輸入框的減少動畫模式支持 */
            @media (prefers-reduced-motion: reduce) {
              .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"]::before,
              .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"]::after {
                animation: none !important;
                transition: none !important;
              }
              
              .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"]:hover {
                transform: none !important;
              }
              
              .gjs-selected input[type="date"], .gjs-selected input[data-input-type="date"] {
                transform: none !important;
              }
            }
            
            /* 日期輸入框的焦點可見性支持 */
            @media (prefers-reduced-motion: no-preference) {
              .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"]:focus-visible {
                outline: 3px solid #1890ff !important;
                outline-offset: 2px !important;
              }
            }
            
            /* 日期輸入框的錯誤狀態樣式 */
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"].error {
              border-color: #ff4d4f !important;
              background-color: #fff2f0 !important;
              color: #ff4d4f !important;
            }
            
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"].error::before {
              content: "❌ 格式錯誤: " attr(data-date-format) !important;
              background-color: rgba(255, 77, 79, 0.9) !important;
            }
            
            /* 日期輸入框的成功狀態樣式 */
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"].success {
              border-color: #52c41a !important;
              background-color: #f6ffed !important;
              color: #52c41a !important;
            }
            
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"].success::before {
              content: "✅ 格式正確: " attr(data-date-format) !important;
              background-color: rgba(82, 196, 0.9) !important;
            }
            
            /* 日期輸入框的警告狀態樣式 */
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"].warning {
              border-color: #faad14 !important;
              background-color: #fffbe6 !important;
              color: #faad14 !important;
            }
            
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"].warning::before {
              content: "⚠️ 格式警告: " attr(data-date-format) !important;
              background-color: rgba(250, 173, 20, 0.9) !important;
            }
            
            /* 日期輸入框的信息狀態樣式 */
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"].info {
              border-color: #1890ff !important;
              background-color: #e6f7ff !important;
              color: #1890ff !important;
            }
            
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"].info::before {
              content: "ℹ️ 格式信息: " attr(data-date-format) !important;
              background-color: rgba(24, 144, 255, 0.9) !important;
            }
            
            /* 日期輸入框的加載狀態樣式 */
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"].loading {
              border-color: #d9d9d9 !important;
              background-color: #fafafa !important;
              color: #bfbfbf !important;
              cursor: wait !important;
            }
            
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"].loading::before {
              content: "⏳ 載入中..." !important;
              background-color: rgba(0, 0, 0, 0.6) !important;
              animation: pulse 1.5s ease-in-out infinite !important;
            }
            
            @keyframes pulse {
              0%, 100% { opacity: 0.6; }
              50% { opacity: 1; }
            }
            
            /* 日期輸入框的驗證狀態樣式 */
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"].validating {
              border-color: #faad14 !important;
              background-color: #fffbe6 !important;
              color: #faad14 !important;
            }
            
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"].validating::before {
              content: "🔍 驗證中..." !important;
              background-color: rgba(250, 173, 20, 0.9) !important;
              animation: spin 1s linear infinite !important;
            }
            
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            
            /* 日期輸入框的完成狀態樣式 */
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"].completed {
              border-color: #52c41a !important;
              background-color: #f6ffed !important;
              color: #52c41a !important;
            }
            
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"].completed::before {
              content: "🎉 完成: " attr(data-date-format) !important;
              background-color: rgba(82, 196, 26, 0.9) !important;
              animation: bounce 0.6s ease-in-out !important;
            }
            
            @keyframes bounce {
              0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
              40% { transform: translateY(-5px); }
              60% { transform: translateY(-3px); }
            }
            
            /* 日期輸入框的焦點樣式 */
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"]:focus {
              outline: none !important;
              border-color: #1890ff !important;
              box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.1) !important;
              transform: scale(1.01) !important;
            }
            
            /* 日期輸入框的幫助提示樣式 */
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"].help {
              border-color: #722ed1 !important;
              background-color: #f9f0ff !important;
              color: #722ed1 !important;
            }
            
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"].help::before {
              content: "💡 幫助: " attr(data-date-format) !important;
              background-color: rgba(114, 46, 209, 0.9) !important;
            }
            
            /* 日期輸入框的禁用樣式 */
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"]:disabled {
              background-color: #f5f5f5 !important;
              color: #999 !important;
              cursor: not-allowed !important;
              opacity: 0.6 !important;
            }
            
            /* 日期輸入框的只讀樣式 */
            .gjs-cv-canvas input[type="date"], .gjs-cv-canvas input[data-input-type="date"][readonly] {
              background-color: #fafafa !important;
              color: #666 !important;
              cursor: default !important;
            }
            
            /* 移除所有 modal 相關樣式，只保留右側面板功能 */
          `;
            if (initialHtmlContent && typeof initialHtmlContent === 'string' && initialHtmlContent.includes('<style>')) {
              const styleMatch = initialHtmlContent.match(/<style[^>]*>([\s\S]*?)<\/style>/);
              if (styleMatch) return styleMatch[1];
            }
            return defaultStyle;
          })(),
        });
        
        console.log('✅ GrapesJS 實例創建成功:', grapesEditor);
        
        // 設置編輯器引用，供 assetManager 使用
        editorRef = grapesEditor;
        
        // 自定義 img 組件類型，禁用內建的雙擊行為
        const domComponents = grapesEditor.DomComponents;
        const imgType = domComponents.getType('image');
        
        if (imgType) {
          // 擴展現有的 image 類型
          domComponents.addType('image', {
            model: {
              defaults: {
                ...imgType.model.defaults,
                // 禁用雙擊時打開 assetManager
                editable: false,
                // 禁用拖動時的 assetManager
                droppable: false,
                traits: [
                  {
                    type: 'button',
                    label: 'Open Asset Manager',
                    text: 'Change Image',
                    command: 'open-assets', // 但我們不提供這個命令
                  }
                ]
              }
            },
            view: {
              // 攔截雙擊事件
              events: {
                dblclick: 'handleDoubleClick'
              },
              handleDoubleClick(e) {
                // 阻止默認行為（打開 assetManager）
                e.preventDefault();
                e.stopPropagation();
                
                // 觸發自定義編輯事件
                const component = this.model;
                if (component && grapesEditor) {
                  grapesEditor.trigger('edit-component-requested', component);
                }
              }
            }
          });
          console.log('✅ 已自定義 img 組件類型，禁用內建雙擊行為');
        } else {
          console.warn('⚠️ 未找到 image 組件類型，無法自定義');
        }
        
        // 配置 table cell (td/th)：editable: false 停用雙擊 RTE 工具列，改由 Style Manager + 右側編輯面板
        const tdType = grapesEditor.DomComponents.getType('td');
        const thType = grapesEditor.DomComponents.getType('th');
        
        // 表格儲存格的拖曳縮放選項：只給右側把手調欄寬、底部把手調列高，
        // 避免角落把手同時改寬高而破壞表格版面。
        const cellResizable = {
          tl: false, tc: false, tr: false,
          cl: false, cr: true,
          bl: false, bc: true, br: false,
          minDim: 20,
          // 儲存格寬度寫在 td/th 上即可帶動整欄寬度
          keyWidth: 'width',
          keyHeight: 'height',
          currentUnit: true,
          unitWidth: 'px',
          unitHeight: 'px'
        };

        if (tdType) {
          grapesEditor.DomComponents.addType('td', {
            extend: 'td',
            model: {
              defaults: {
                editable: false, // 停用雙擊 RTE 工具列，改由右側面板編輯內容、Style Manager 設定樣式
                droppable: true, // 允許拖放其他組件
                selectable: true, // 可以選中
                highlightable: true, // 可以高亮
                resizable: cellResizable // 允許滑鼠拖曳調整欄寬/列高
              }
            }
          });
          console.log('✅ 已配置 td 組件（雙擊不再顯示 RTE 工具列，支援拖曳縮放）');
        }
        
        if (thType) {
          grapesEditor.DomComponents.addType('th', {
            extend: 'th',
            model: {
              defaults: {
                editable: false, // 停用雙擊 RTE 工具列，改由右側面板編輯內容、Style Manager 設定樣式
                droppable: true, // 允許拖放其他組件
                selectable: true, // 可以選中
                highlightable: true, // 可以高亮
                resizable: cellResizable // 允許滑鼠拖曳調整欄寬/列高
              }
            }
          });
          console.log('✅ 已配置 th 組件（雙擊不再顯示 RTE 工具列，支援拖曳縮放）');
        }
        
        // 添加自定義功能
        addCustomBlocks(grapesEditor);
        addEditFunctionality(grapesEditor);
        addDeselectFunctionality(grapesEditor);
        addRteToolbarFormatActions(grapesEditor, resources);

        // 隱藏 Style Manager、Settings 和 Layer Manager 按鈕，並默認打開 blocks 面板
        setTimeout(() => {
          try {
            const styleManagerBtn = document.querySelector('.gjs-pn-btn[title="Open Style Manager"]');
            const settingsBtn = document.querySelector('.gjs-pn-btn[title="Settings"]');
            const layerManagerBtn = document.querySelector('.gjs-pn-btn[title="Open Layer Manager"]');
            
            if (styleManagerBtn) {
              styleManagerBtn.style.display = ''; // 顯示 Style Manager，供 Table/TableCell 設定線框樣式
              console.log('✅ Style Manager 按鈕已顯示（可設定邊框等樣式）');
            }
            
            if (settingsBtn) {
              settingsBtn.style.display = 'none';
              console.log('✅ Settings 按鈕已隱藏');
            }
            
            if (layerManagerBtn) {
              layerManagerBtn.style.display = ''; // 顯示 Layer Manager，可從元件樹選中整個 table
              console.log('✅ Layer Manager 按鈕已顯示（可從元件樹選中表格）');
            }
            
            // RTE 工具列：全域隱藏，改由 Style Manager 設定樣式
            if (!document.getElementById('gjs-rte-toolbar-hide')) {
              const rteStyle = document.createElement('style');
              rteStyle.id = 'gjs-rte-toolbar-hide';
              rteStyle.textContent = `
                .gjs-rte-actionbar, .gjs-rte-toolbar { display: none !important; }
              `;
              document.head.appendChild(rteStyle);
            }
            
            // 默認打開 blocks 面板
            const blocksBtn = document.querySelector('.gjs-pn-btn[title="Open Blocks"]');
            if (blocksBtn) {
              blocksBtn.click();
              console.log('✅ 默認打開 blocks 面板');
            } else {
              console.log('⚠️ 未找到 blocks 按鈕');
            }
          } catch (error) {
            console.warn('⚠️ 隱藏按鈕或打開 blocks 面板時出錯:', error);
          }
        }, 100);

        console.log('🔧 設置編輯器狀態...');
        setEditor(grapesEditor);
        setIsReady(true);
        
        console.log('✅ GrapesJS 編輯器初始化成功');
        console.log('🎧 檢查 onEditorReady 回調:', {
          hasOnEditorReady: !!onEditorReady,
          onEditorReadyType: onEditorReady ? typeof onEditorReady : 'null'
        });
        
        // 通知父組件編輯器已準備好
        if (onEditorReady) {
          console.log('🎧 調用 onEditorReady 回調...');
          try {
            onEditorReady(grapesEditor);
            console.log('✅ onEditorReady 回調調用成功');
          } catch (error) {
            console.error('❌ onEditorReady 回調調用失敗:', error);
          }
        } else {
          console.log('⚠️ 沒有 onEditorReady 回調，跳過調用');
        }
        
      } catch (error) {
        console.error('❌ GrapesJS 初始化失敗:', error);
        setIsReady(false);
      }
    }).catch(error => {
      console.error('❌ 載入語言資源失敗:', error);
      setIsReady(false);
    });

    return () => {
      if (editor) {
        console.log('🧹 清理 GrapesJS 編輯器');
        editor.destroy();
      }
    };
  }, [initialHtmlContent, onEditorReady]);

  // 添加 setContent 方法來支持外部內容更新
  const setContent = useCallback((htmlContent) => {
    if (editor && htmlContent) {
      console.log('🔄 設置新內容到編輯器開始');
      console.log('📝 原始內容長度:', htmlContent.length);
      console.log('📝 原始內容前100字符:', htmlContent.substring(0, 100) + '...');
      
      try {
        // 分離 HTML 和 CSS
        let html = htmlContent;
        let css = '';
        
        console.log('🔍 檢查是否包含樣式標籤...');
        console.log('🔍 包含 <style> 標籤:', htmlContent.includes('<style>'));
        
        // 檢查是否包含樣式標籤
        if (htmlContent.includes('<style>')) {
          console.log('🔍 開始解析樣式標籤...');
          const styleMatch = htmlContent.match(/<style[^>]*>([\s\S]*?)<\/style>/);
          if (styleMatch) {
            css = styleMatch[1];
            html = htmlContent.replace(/<style[^>]*>[\s\S]*?<\/style>/g, '');
            console.log('✅ 樣式解析成功');
            console.log('🎨 CSS 內容長度:', css.length);
            console.log('📄 HTML 內容長度:', html.length);
          } else {
            console.log('⚠️ 樣式標籤匹配失敗');
          }
        } else {
          console.log('ℹ️ 未檢測到樣式標籤，使用原始內容作為 HTML');
        }
        
        // 設置 HTML 內容前，檢查 img 標籤
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        const imgTagsBefore = tempDiv.querySelectorAll('img');
        console.log(`🔍 設置 HTML 前，包含 ${imgTagsBefore.length} 個 img 標籤`);
        imgTagsBefore.forEach((img, index) => {
          const src = img.getAttribute('src');
          console.log(`  img ${index + 1} src:`, src ? src.substring(0, 100) : '無 src');
        });
        
        // 設置 HTML 內容
        console.log('🔄 開始設置 HTML 內容...');
        console.log('🔄 調用 editor.setComponents()...');
        editor.setComponents(html);
        console.log('✅ HTML 內容設置完成');
        
        // 設置後驗證 img 標籤
        setTimeout(() => {
          const allComponents = editor.DomComponents.getComponents();
          const imgComponents = [];
          const traverse = (comp) => {
            if (comp && comp.get) {
              const tagName = comp.get('tagName');
              if (tagName && tagName.toLowerCase() === 'img') {
                imgComponents.push(comp);
              }
              const children = comp.components ? comp.components() : [];
              children.forEach(traverse);
            }
          };
          traverse(allComponents);
          
          console.log(`🔍 設置 HTML 後，編輯器中有 ${imgComponents.length} 個 img 組件`);
          imgComponents.forEach((comp, index) => {
            const src = comp.getAttributes().src || '';
            console.log(`  img 組件 ${index + 1} src:`, src ? src.substring(0, 100) : '無 src');
          });
        }, 500);
        
        // 設置 CSS 樣式
        if (css) {
          console.log('🔄 開始設置 CSS 樣式...');
          console.log('🔄 調用 editor.setStyle()...');
          editor.setStyle(css);
          console.log('✅ CSS 樣式設置完成');
        } else {
          console.log('ℹ️ 無 CSS 樣式需要設置');
        }
        
        // 驗證設置結果
        console.log('🔍 驗證設置結果...');
        const currentHtml = editor.getHtml();
        const currentCss = editor.getCss();
        console.log('📊 當前編輯器 HTML 長度:', currentHtml.length);
        console.log('📊 當前編輯器 CSS 長度:', currentCss.length);
        
        console.log('✅ 內容設置成功完成');
      } catch (error) {
        console.error('❌ 設置內容時發生錯誤:', error);
        console.error('❌ 錯誤堆疊:', error.stack);
        
        // 如果分離失敗，嘗試直接設置
        console.log('🔄 嘗試備用方法：直接設置原始內容...');
        try {
          editor.setComponents(htmlContent);
          console.log('✅ 備用方法成功');
        } catch (fallbackError) {
          console.error('❌ 備用方法也失敗:', fallbackError);
        }
      }
    } else {
      console.log('⚠️ setContent 調用失敗：', {
        hasEditor: !!editor,
        hasContent: !!htmlContent,
        contentLength: htmlContent ? htmlContent.length : 0
      });
    }
  }, [editor]);

  return { 
    editor, 
    isReady, 
    languageResources, 
    setContent 
  };
};

const addCustomBlocks = (editor) => {
  const blockManager = editor.BlockManager;
  
  // 獲取語言資源
  const userLanguage = localStorage.getItem('language') || 'zh-TC';
  let resources;
  
  // 根據語言設置獲取對應的翻譯
  switch (userLanguage) {
    case 'zh-SC':
      resources = require('../locales/zh-SC.js').default;
      break;
    case 'zh-TC':
      resources = require('../locales/zh-TC.js').default;
      break;
    case 'en':
      resources = require('../locales/en.js').default;
      break;
    default:
      resources = require('../locales/zh-TC.js').default;
  }
  
  const t = resources.eformDesigner?.grapesJs || {};
  
  // 佈局元素
  blockManager.add('section', {
    label: t.section || 'Section',
    category: t.layout || 'Layout',
    content: '<section class="section"><h2>This is a section</h2><p>This is a box</p></section>',
    media: '<svg viewBox="0 0 24 24"><path d="M2 20h20V4H2v16zm18-2V6H4v12h16z"/></svg>'
  });
  
  blockManager.add('div', {
    label: t.div || 'Div',
    category: t.layout || 'Layout',
    content: '<div class="div-block"><p>This is a div block</p></div>',
    media: '<svg viewBox="0 0 24 24"><path d="M3 3h18v18H3V3zm16 16V5H5v14h14z"/></svg>'
  });
  
  blockManager.add('container', {
    label: t.container || 'Container',
    category: t.layout || 'Layout',
    content: '<div class="container"><div class="container-content"></div></div>',
    media: '<svg viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14z"/></svg>'
  });
  
  // 文字元素
  blockManager.add('text', {
    label: t.text || 'Text',
    category: t.basic || 'Basic',
    content: '<div data-gjs-type="text">Insert your text here</div>',
    media: '<svg viewBox="0 0 24 24"><path d="M2.5 4v3h5v12h3V7h5V4H2.5zM21.5 9h-9v3h3v7h3v-7h3V9z"/></svg>'
  });
  
  blockManager.add('heading', {
    label: t.heading || 'Heading',
    category: t.basic || 'Basic',
    content: '<h2>Insert your heading here</h2>',
    media: '<svg viewBox="0 0 24 24"><path d="M6 3h2v18H6zm3.5 12l3-6 3 6H9.5z"/></svg>'
  });
  
  blockManager.add('paragraph', {
    label: t.paragraph || 'Paragraph',
    category: t.basic || 'Basic',
    content: '<p>Insert your paragraph here</p>',
    media: '<svg viewBox="0 0 24 24"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>'
  });
  
  blockManager.add('image', {
    label: t.image || 'Image',
    category: t.basic || 'Basic',
    content: '<img src="/assets/no-image.jpg" alt="Image" width="100px" height="50px" style="max-width: 100%;" />',
    media: '<svg viewBox="0 0 24 24"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>'
  });
  
  // Table 配置函數
  // colWidths: [{ value: '100', unit: 'px' }, { value: '50', unit: '%' }, ...]
  const createTableHTML = (rows, cols, headers, colWidths = []) => {
    const cellStyle = 'border: 1px solid #ddd; padding: 8px;';
    let html = '<table style="width: 100%; border-collapse: collapse;">';
    
    // 添加 colgroup（如果有任何欄寬設置）
    const hasWidths = colWidths.some(w => w && w.value !== '' && w.value !== null && w.value !== undefined);
    if (hasWidths) {
      html += '<colgroup>';
      for (let i = 0; i < cols; i++) {
        const w = colWidths[i];
        if (w && w.value !== '' && w.value !== null && w.value !== undefined) {
          html += `<col style="width: ${w.value}${w.unit || 'px'};">`;
        } else {
          html += '<col>';
        }
      }
      html += '</colgroup>';
    }
    
    // 添加 thead（如果有 headers）
    if (headers && headers.length > 0 && headers.some(h => h && h.trim())) {
      html += '<thead><tr>';
      for (let i = 0; i < cols; i++) {
        const headerText = (headers[i] && headers[i].trim()) || `Header ${i + 1}`;
        html += `<th style="${cellStyle}">${headerText}</th>`;
      }
      html += '</tr></thead>';
    }
  
    // 添加 tbody
    html += '<tbody>';
    for (let r = 0; r < rows; r++) {
      html += '<tr>';
      for (let c = 0; c < cols; c++) {
        html += `<td style="${cellStyle}">Cell ${r + 1}-${c + 1}</td>`;
      }
      html += '</tr>';
    }
    html += '</tbody></table>';
    
    return html;
  };
  
  // 存儲 table 配置狀態
  let tableConfigPending = false;
  let pendingTableComponent = null;
  
  // 從現有 table 組件讀取配置
  const readTableConfig = (tableComponent) => {
    if (!tableComponent || !tableComponent.get) return null;
    
    try {
      const thead = tableComponent.components().find(c => c.get('tagName') === 'thead');
      const tbody = tableComponent.components().find(c => c.get('tagName') === 'tbody');
      const colgroup = tableComponent.components().find(c => c.get('tagName') === 'colgroup');
      
      let rows = 0;
      let cols = 0;
      let headers = [];
      let hasHeader = false;
      let colWidths = [];
      
      if (tbody) {
        const tbodyRows = tbody.components().toArray();
        rows = tbodyRows.length;
        if (rows > 0) {
          const firstRow = tbodyRows[0];
          const cells = firstRow.components().toArray();
          cols = cells.length;
        }
      }
      
      if (thead) {
        hasHeader = true;
        const theadRows = thead.components().toArray();
        if (theadRows.length > 0) {
          const headerRow = theadRows[0];
          const headerCells = headerRow.components().toArray();
          headers = headerCells.map(cell => {
            const el = cell.getEl();
            return el ? (el.textContent || el.innerText || '') : '';
          });
        }
      }
      
      // 從 colgroup 讀取欄寬（優先用 GrapesJS 組件模型 getStyle，避免依賴 DOM 渲染時序）
      colWidths = Array(cols).fill(null).map(() => ({ value: '', unit: 'px' }));
      if (colgroup) {
        const colEls = colgroup.components().toArray();
        colEls.forEach((col, i) => {
          if (i >= cols) return;
          let widthValue = '';
          
          // 優先從 GrapesJS 組件模型讀取
          try {
            const colStyle = col.getStyle ? col.getStyle() : {};
            widthValue = colStyle.width || '';
          } catch (e) {}
          
          // 備用：從 DOM 元素讀取
          if (!widthValue) {
            try {
              const el = col.getEl();
              widthValue = (el && el.style && el.style.width) || '';
            } catch (e) {}
          }
          
          if (widthValue) {
            const isPercent = widthValue.includes('%');
            const val = parseFloat(widthValue);
            colWidths[i] = { value: isNaN(val) ? '' : String(val), unit: isPercent ? '%' : 'px' };
          }
        });
      }
      
      return { rows, cols, headers, hasHeader, colWidths };
    } catch (error) {
      console.warn('⚠️ 讀取 table 配置時出錯:', error);
      return null;
    }
  };
  
  // 顯示 Table 配置 Modal
  const showTableConfigModal = (editor, existingTableComponent = null) => {
    const { Modal, InputNumber, Input, Checkbox, Select } = require('antd');
    
    // 獲取語言資源（在函數內部獲取，確保可以訪問）
    const userLanguage = localStorage.getItem('language') || 'zh-TC';
    let modalResources;
    switch (userLanguage) {
      case 'zh-SC':
        modalResources = require('../locales/zh-SC.js').default;
        break;
      case 'zh-TC':
        modalResources = require('../locales/zh-TC.js').default;
        break;
      case 'en':
        modalResources = require('../locales/en.js').default;
        break;
      default:
        modalResources = require('../locales/zh-TC.js').default;
    }
    const modalT = modalResources.eformDesigner?.grapesJs || {};
    
    // 讀取現有配置（如果有的話）
    let initialRows = 3;
    let initialCols = 3;
    let initialHeaders = ['', '', ''];
    let initialHasHeader = true;
    let initialColWidths = Array(3).fill(null).map(() => ({ value: '', unit: 'px' }));
    
    if (existingTableComponent) {
      const existingConfig = readTableConfig(existingTableComponent);
      if (existingConfig) {
        initialRows = existingConfig.rows || 3;
        initialCols = existingConfig.cols || 3;
        initialHeaders = existingConfig.headers || Array(initialCols).fill('');
        initialHasHeader = existingConfig.hasHeader !== false;
        initialColWidths = existingConfig.colWidths || Array(initialCols).fill(null).map(() => ({ value: '', unit: 'px' }));
      }
    }
    
    // 創建一個臨時容器來渲染 Modal
    const modalContainer = document.createElement('div');
    modalContainer.id = 'table-config-modal-container';
    document.body.appendChild(modalContainer);
    const root = ReactDOM.createRoot(modalContainer);
    
    const TableConfigContent = () => {
      const [rows, setRows] = React.useState(initialRows);
      const [cols, setCols] = React.useState(initialCols);
      const [headers, setHeaders] = React.useState(initialHeaders);
      const [hasHeader, setHasHeader] = React.useState(initialHasHeader);
      const [colWidths, setColWidths] = React.useState(initialColWidths);
      
      React.useEffect(() => {
        if (headers.length !== cols) {
          const newHeaders = Array(cols).fill('').map((_, i) => headers[i] || '');
          setHeaders(newHeaders);
        }
        if (colWidths.length !== cols) {
          const newWidths = Array(cols).fill(null).map((_, i) => colWidths[i] || { value: '', unit: 'px' });
          setColWidths(newWidths);
        }
      }, [cols]);
      
      const handleOk = () => {
        const finalHeaders = hasHeader ? headers : [];
        const tableHTML = createTableHTML(rows, cols, finalHeaders, colWidths);
        
        if (existingTableComponent) {
          // 更新現有的 table 組件
          try {
            // 獲取父組件
            const parent = existingTableComponent.parent();
            if (parent) {
              // 獲取組件在父組件中的索引
              const components = parent.components();
              let componentIndex = -1;
              for (let i = 0; i < components.length; i++) {
                if (components[i] === existingTableComponent) {
                  componentIndex = i;
                  break;
                }
              }
              
              if (componentIndex >= 0) {
                // 移除舊的 table
                existingTableComponent.remove();
                // 添加新的 table
                parent.components().add(tableHTML, { at: componentIndex });
                console.log('✅ 已更新現有 table 組件');
              } else {
                // 如果找不到索引，直接替換
                existingTableComponent.replaceWith(tableHTML);
              }
            } else {
              // 如果沒有父組件，直接替換
              existingTableComponent.replaceWith(tableHTML);
            }
          } catch (error) {
            console.warn('⚠️ 更新 table 組件時出錯，嘗試添加新組件:', error);
            editor.Components.addComponent(tableHTML);
          }
        } else {
          // 將表格添加到編輯器
          editor.Components.addComponent(tableHTML);
        }
        
        // 清理
        root.unmount();
        setTimeout(() => {
          const container = document.getElementById('table-config-modal-container');
          if (container && document.body.contains(container)) {
            document.body.removeChild(container);
          }
        }, 100);
      };
      
      const handleCancel = () => {
        // 如果是新添加的 table（pendingTableComponent），取消時移除它
        if (pendingTableComponent && !existingTableComponent) {
          try {
            pendingTableComponent.remove();
            console.log('✅ 已移除未配置的 table 組件');
          } catch (error) {
            console.warn('⚠️ 移除 table 組件時出錯:', error);
          }
          pendingTableComponent = null;
        }
        
        root.unmount();
        setTimeout(() => {
          const container = document.getElementById('table-config-modal-container');
          if (container && document.body.contains(container)) {
            document.body.removeChild(container);
          }
        }, 100);
      };
      
      return React.createElement(Modal, {
        title: modalT.tableConfig || '表格設置',
        open: true,
        onOk: handleOk,
        onCancel: handleCancel,
        okText: modalT.confirm || '確認',
        cancelText: modalT.cancel || '取消',
        width: 620
      },
        React.createElement('div', { style: { padding: '16px 0' } },
          React.createElement('div', { style: { marginBottom: '16px' } },
            React.createElement('label', { style: { display: 'block', marginBottom: '8px', fontWeight: 'bold' } },
              modalT.tableRows || '行數:'
            ),
            React.createElement(InputNumber, {
              min: 1,
              max: 20,
              value: rows,
              onChange: setRows,
              style: { width: '100%' }
            })
          ),
          React.createElement('div', { style: { marginBottom: '16px' } },
            React.createElement('label', { style: { display: 'block', marginBottom: '8px', fontWeight: 'bold' } },
              modalT.tableCols || '列數:'
            ),
            React.createElement(InputNumber, {
              min: 1,
              max: 20,
              value: cols,
              onChange: setCols,
              style: { width: '100%' }
            })
          ),
          React.createElement(Checkbox, {
            checked: hasHeader,
            onChange: (e) => setHasHeader(e.target.checked),
            style: { marginBottom: '16px' }
          }, modalT.tableHasHeader || '包含表頭'),
          hasHeader && React.createElement('div', { style: { marginBottom: '16px' } },
            React.createElement('label', { style: { display: 'block', marginBottom: '8px', fontWeight: 'bold' } },
              modalT.tableHeaders || '表頭文字:'
            ),
            ...Array.from({ length: cols }, (_, i) =>
              React.createElement(Input, {
                key: i,
                placeholder: `${modalT.tableHeader || '表頭'} ${i + 1}`,
                value: headers[i] || '',
                onChange: (e) => {
                  const newHeaders = [...headers];
                  newHeaders[i] = e.target.value;
                  setHeaders(newHeaders);
                },
                style: { marginBottom: '8px' }
              })
            )
          ),
          React.createElement('div', { style: { marginBottom: '8px' } },
            React.createElement('label', { style: { display: 'block', marginBottom: '8px', fontWeight: 'bold' } },
              modalT.tableColWidths || '列寬設置:'
            ),
            React.createElement('div', {
              style: {
                display: 'grid',
                gridTemplateColumns: 'auto 1fr 80px',
                gap: '6px 8px',
                alignItems: 'center'
              }
            },
              // 標題列
              React.createElement('span', { style: { color: '#888', fontSize: '12px' } }, ''),
              React.createElement('span', { style: { color: '#888', fontSize: '12px' } }, modalT.tableColWidthValue || '寬度（留空為自動）'),
              React.createElement('span', { style: { color: '#888', fontSize: '12px' } }, modalT.tableColWidthUnit || '單位'),
              // 每欄輸入列
              ...Array.from({ length: cols }, (_, i) => [
                React.createElement('span', {
                  key: `label-${i}`,
                  style: { fontWeight: 500, whiteSpace: 'nowrap' }
                }, `${modalT.tableColLabel || '列'} ${i + 1}:`),
                React.createElement(InputNumber, {
                  key: `width-${i}`,
                  min: 0,
                  value: (colWidths[i] && colWidths[i].value !== '') ? Number(colWidths[i].value) : null,
                  placeholder: modalT.tableColWidthAuto || '自動',
                  onChange: (val) => {
                    const newWidths = [...colWidths];
                    newWidths[i] = { ...(newWidths[i] || { unit: 'px' }), value: val === null ? '' : String(val) };
                    setColWidths(newWidths);
                  },
                  style: { width: '100%' }
                }),
                React.createElement(Select, {
                  key: `unit-${i}`,
                  value: (colWidths[i] && colWidths[i].unit) || 'px',
                  onChange: (val) => {
                    const newWidths = [...colWidths];
                    newWidths[i] = { ...(newWidths[i] || { value: '' }), unit: val };
                    setColWidths(newWidths);
                  },
                  style: { width: '100%' },
                  options: [
                    { value: 'px', label: 'px' },
                    { value: '%', label: '%' }
                  ]
                })
              ]).flat()
            )
          )
        )
      );
    };
    
    root.render(React.createElement(TableConfigContent));
  };
  
  blockManager.add('table', {
    label: t.table || 'Table',
    category: t.basic || 'Basic',
    content: createTableHTML(3, 3, ['Header 1', 'Header 2', 'Header 3']),
    media: '<svg viewBox="0 0 24 24"><path d="M3 3h18v18H3V3zm16 16V5H5v14h14zM7 7h10v2H7V7zm0 4h10v2H7v-2zm0 4h7v2H7v-2z"/></svg>',
    activate: true,
    select: true
  });
  
  // 監聽 component:add 事件，攔截 table 的添加
  editor.on('component:add', (component) => {
    if (tableConfigPending && component && component.get) {
      const tagName = component.get('tagName');
      if (tagName && tagName.toLowerCase() === 'table') {
        // 保存 table 組件引用，但不移除它（讓它先存在）
        pendingTableComponent = component;
        tableConfigPending = false;
        
        // 顯示配置界面（傳入現有的 table 組件）
        showTableConfigModal(editor, component);
      }
    }
  });
  
  // 監聽 block 拖拽開始，標記 table block
  editor.on('block:drag', (block) => {
    if (block && block.get && block.get('id') === 'table') {
      tableConfigPending = true;
      pendingTableComponent = null; // 重置
    }
  });
  
  // 監聽 block 拖拽停止，如果取消則清除標記
  editor.on('block:drag:stop', () => {
    // 延遲檢查，如果沒有添加 table 組件，清除標記
    setTimeout(() => {
      if (tableConfigPending) {
        tableConfigPending = false;
        pendingTableComponent = null;
      }
    }, 500);
  });
  
  // 添加命令：當選中 table 時可以重新配置
  editor.Commands.add('configure-table', {
    run: function(editor, sender, options) {
      const selected = editor.getSelected();
      if (selected && selected.get) {
        const tagName = selected.get('tagName');
        if (tagName && tagName.toLowerCase() === 'table') {
          console.log('🔧 重新配置 table 組件');
          showTableConfigModal(editor, selected);
        } else {
          console.warn('⚠️ 選中的組件不是 table');
        }
      }
    }
  });

  // 選中父級表格：當選中 td/th 時，可一鍵選中整個 table（GrapesJS 官方 parent() 遍歷）
  editor.Commands.add('select-parent-table', {
    run: function(editor, sender, options) {
      const selected = editor.getSelected();
      if (!selected || typeof selected.parent !== 'function') return;
      const tagName = selected.get && selected.get('tagName');
      if (tagName !== 'td' && tagName !== 'th') return;
      let p = selected.parent();
      while (p && p.get) {
        const pTag = p.get('tagName');
        if (pTag && pTag.toLowerCase() === 'table') {
          editor.select(p);
          return;
        }
        p = typeof p.parent === 'function' ? p.parent() : null;
      }
    }
  });
  
  // 為 table 組件添加自定義工具欄按鈕（配置按鈕）
  // 注意：這裡的 t 是在 addCustomBlocks 函數作用域內的
  editor.DomComponents.addType('table', {
    model: {
      defaults: {
        // 允許滑鼠拖曳調整整張表格的寬度/高度（右、下、右下角把手）
        resizable: {
          tl: false, tc: false, tr: false,
          cl: false, cr: true,
          bl: false, bc: true, br: true,
          minDim: 30,
          keyWidth: 'width',
          keyHeight: 'height',
          currentUnit: true,
          unitWidth: 'px',
          unitHeight: 'px'
        },
        traits: [
          {
            type: 'button',
            label: t.tableConfig || '表格設置',
            text: t.tableConfig || '表格設置',
            command: 'configure-table'
          }
        ]
      }
    }
  });
  
  // 表單元素
  blockManager.add('form', {
    label: t.form || 'Form',
    category: t.forms || 'Forms',
    content: '<form class="form"><input type="text" placeholder="Enter text here" /></form>',
    media: '<svg viewBox="0 0 24 24"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm2 16H8v-2h8v2zm0-4H8v-2h8v2zm-3-5V3.5L18.5 9H13z"/></svg>'
  });
  
  blockManager.add('input', {
    label: t.input || 'Input',
    category: t.forms || 'Forms',
    content: '<input type="text" placeholder="Enter text here" />',
    media: '<svg viewBox="0 0 24 24"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-5 14H4v-4h11v4zm0-5H4V9h11v4zm5 5h-4V9h4v9z"/></svg>'
  });
  
  blockManager.add('button', {
    label: t.button || 'Button',
    category: t.forms || 'Forms',
    content: '<button class="button">Click me</button>',
    media: '<svg viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>'
  });
  
  blockManager.add('textarea', {
    label: t.textarea || 'Textarea',
    category: t.forms || 'Forms',
    content: `<textarea placeholder="${resources.eformDesigner?.pleaseEnterYourMessage || '請輸入您的訊息'}" rows="4" cols="50"></textarea>`,
    media: '<svg viewBox="0 0 24 24"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm-5 14H4v-4h11v4zm0-5H4V9h11v4zm5 5h-4V9h4v9z"/></svg>'
  });
  
  // 更多表單控件
  blockManager.add('select', {
    label: t.select || 'Select',
    category: t.forms || 'Forms',
    content: `<select><option value="">${resources.eformDesigner?.pleaseSelect || '請選擇...'}</option><option value="option1">${resources.eformDesigner?.option1 || '選項 1'}</option><option value="option2">${resources.eformDesigner?.option2 || '選項 2'}</option><option value="option3">${resources.eformDesigner?.option3 || '選項 3'}</option></select>`,
    media: '<svg viewBox="0 0 24 24"><path d="M7 10l5 5 5-5z"/><path d="M0 0h24v24H0z" fill="none"/></svg>'
  });
  
  blockManager.add('radio', {
    label: t.radio || 'Radio',
    category: t.forms || 'Forms',
    content: `<div><label><input type="radio" name="radio1" value="option1" /> ${resources.eformDesigner?.option1 || '選項 1'}</label><br/><label><input type="radio" name="radio1" value="option2" /> ${resources.eformDesigner?.option2 || '選項 2'}</label><br/><label><input type="radio" name="radio1" value="option3" /> ${resources.eformDesigner?.option3 || '選項 3'}</label></div>`,
    media: '<svg viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>'
  });
  
  blockManager.add('checkbox', {
    label: t.checkbox || 'Checkbox',
    category: t.forms || 'Forms',
    content: `<div><label><input type="checkbox" value="check1" /> ${resources.eformDesigner?.option1 || '選項 1'}</label><br/><label><input type="checkbox" value="check2" /> ${resources.eformDesigner?.option2 || '選項 2'}</label><br/><label><input type="checkbox" value="check3" /> ${resources.eformDesigner?.option3 || '選項 3'}</label></div>`,
    media: '<svg viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-9 14l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/></svg>'
  });
  
  blockManager.add('file-upload', {
    label: t.fileUpload || 'File Upload',
    category: t.forms || 'Forms',
    content: '<input type="file" />',
    media: '<svg viewBox="0 0 24 24"><path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11z"/></svg>'
  });
  
  blockManager.add('email', {
    label: t.email || 'Email',
    category: t.forms || 'Forms',
    content: `<input type="email" placeholder="${resources.eformDesigner?.pleaseEnterEmail || '請輸入電子郵件'}" />`,
    media: '<svg viewBox="0 0 24 24"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>'
  });
  
  blockManager.add('password', {
    label: t.password || 'Password',
    category: t.forms || 'Forms',
    content: `<input type="password" placeholder="${resources.eformDesigner?.pleaseEnterPassword || '請輸入密碼'}" />`,
    media: '<svg viewBox="0 0 24 24"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>'
  });
  
  blockManager.add('number', {
    label: t.number || 'Number',
    category: t.forms || 'Forms',
    content: `<input type="number" placeholder="${resources.eformDesigner?.pleaseEnterNumber || '請輸入數字'}" />`,
    media: '<svg viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>'
  });
  
  blockManager.add('currency', {
    label: resources.eformDesigner?.currency || 'Currency',
    category: t.forms || 'Forms',
    content: `<input type="text" data-input-type="currency" inputmode="decimal" placeholder="${resources.eformDesigner?.pleaseEnterCurrencyAmount || '請輸入金額'}" />`,
    media: '<svg viewBox="0 0 24 24"><path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z"/></svg>'
  });
  
  blockManager.add('date', {
    label: t.date || 'Date',
    category: t.forms || 'Forms',
    content: '<input type="text" data-input-type="date" data-date-format="YYYY-MM-DD" placeholder="" />',
    media: '<svg viewBox="0 0 24 24"><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/></svg>'
  });

  blockManager.add('month', {
    label: t.month || 'Month',
    category: t.forms || 'Forms',
    content: `<input type="month" placeholder="${resources.eformDesigner?.pleaseSelectMonth || '請選擇月份'}" />`,
    media: '<svg viewBox="0 0 24 24"><path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2zm-8 4h2v2H7zm4 0h2v2h-2z"/></svg>'
  });
  
  blockManager.add('time', {
    label: t.time || 'Time',
    category: t.forms || 'Forms',
    content: '<input type="time" />',
    media: '<svg viewBox="0 0 24 24"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>'
  });
  
  blockManager.add('url', {
    label: t.url || 'URL',
    category: t.forms || 'Forms',
    content: `<input type="url" placeholder="${resources.eformDesigner?.pleaseEnterUrl || '請輸入網址'}" />`,
    media: '<svg viewBox="0 0 24 24"><path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1s1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/></svg>'
  });
  
  blockManager.add('tel', {
    label: t.tel || 'Phone',
    category: t.forms || 'Forms',
    content: `<input type="tel" placeholder="${resources.eformDesigner?.pleaseEnterPhoneNumber || '請輸入電話號碼'}" />`,
    media: '<svg viewBox="0 0 24 24"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/></svg>'
  });
  
  blockManager.add('label', {
    label: t.label || 'Label',
    category: t.forms || 'Forms',
    content: `<label for="input1">${resources.eformDesigner?.labelText || '標籤文字'}</label>`,
    media: '<svg viewBox="0 0 24 24"><path d="M17.63 5.84C17.27 5.33 16.67 5 16 5L5 5.01C3.9 5.01 3 5.9 3 7v10c0 1.1.9 1.99 2 1.99L16 19c.67 0 1.27-.33 1.63-.84L22 12l-4.37-6.16z"/></svg>'
  });
  
  blockManager.add('fieldset', {
    label: t.fieldset || 'Fieldset',
    category: t.forms || 'Forms',
    content: `<fieldset><legend>${resources.eformDesigner?.groupTitle || '群組標題'}</legend><input type="text" placeholder="${resources.eformDesigner?.groupInputField || '群組內輸入框'}" /></fieldset>`,
    media: '<svg viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>'
  });
  
  console.log('✅ 已添加 28 個自定義區塊（包含 table）');
};

// 移除不再需要的通用表單組件創建函數，因為這些功能現在在右側面板中處理

const addEditFunctionality = (editor) => {
  console.log('🔧 開始添加編輯功能...');
  
  // 獲取語言資源
  const userLanguage = localStorage.getItem('language') || 'zh-TC';
  let resources;
  
  // 根據語言設置獲取對應的翻譯
  switch (userLanguage) {
    case 'zh-SC':
      resources = require('../locales/zh-SC.js').default;
      break;
    case 'zh-TC':
      resources = require('../locales/zh-TC.js').default;
      break;
    case 'en':
      resources = require('../locales/en.js').default;
      break;
    default:
      resources = require('../locales/zh-TC.js').default;
  }
  
  // 初始化表單數據函數 - 保留用於右側面板
  const initializeFormData = (component) => {
    // 添加安全檢查
    if (!component || !component.get || typeof component.get !== 'function') {
      console.warn('⚠️ initializeFormData: 組件無效或已被銷毀');
      return;
    }
    
    try {
      const tagName = component.get('tagName');
      if (!tagName) {
        console.warn('⚠️ initializeFormData: 組件標籤名稱無效');
        return;
      }
      
      console.log('🔧 初始化表單數據:', tagName);
      
      // 根據組件類型初始化不同的表單數據
      const attributes = component.getAttributes();
      
      switch (tagName.toLowerCase()) {
        case 'input':
          // 將數據存儲到組件本身，供右側面板使用
          const inputType = attributes['data-input-type'] === 'currency' ? 'currency'
            : (attributes['data-input-type'] === 'date' ? 'date' : (attributes.type || 'text'));
          
          // 優先獲取實際顯示的內容
          let inputValue = '';
          try {
            // 優先從 DOM 元素獲取 value 屬性，這是最可靠的方法
            const el = component.getEl ? component.getEl() : null;
            if (el) {
              inputValue = el.value || el.getAttribute('value') || '';
              console.log('✅ 從 DOM 獲取 input value:', inputValue);
            }
            
            // 如果 DOM 方法失敗，嘗試其他方法
            if (!inputValue) {
              inputValue = component.get('content') || attributes.value || '';
              console.log('⚠️ 使用備用方法獲取 input value:', inputValue);
            }
          } catch (error) {
            console.warn('⚠️ 獲取 input value 時出錯:', error);
            inputValue = attributes.value || '';
          }
          
          const editFormData = {
            type: inputType,
            placeholder: attributes.placeholder || '',
            value: inputValue,
            name: attributes.name || '',
            required: attributes.required || false,
            disabled: attributes.disabled || false,
            readonly: attributes.readonly || false,
            maxlength: attributes.maxlength || '',
            minlength: attributes.minlength || '',
            pattern: attributes.pattern || '',
            title: attributes.title || ''
          };
          
          // 如果是日期類型，添加日期格式信息
          if (inputType === 'date') {
            editFormData.dateFormat = attributes['data-date-format'] || 'YYYY-MM-DD';
          }
          // 金額類型無需額外屬性，Cleave.js 會在表單填寫頁套用
          
          component.set('editFormData', editFormData);
          console.log('🔧 input 組件數據初始化完成:', editFormData);
          break;
          
        case 'textarea':
          // 優先從 DOM 元素獲取 textContent，這是最可靠的方法
          let textareaValue = '';
          try {
            const el = component.getEl ? component.getEl() : null;
            if (el) {
              console.log(' textarea DOM 元素:', el);
              console.log('🔍 textarea DOM 元素標籤:', el.tagName);
              console.log('🔍 textarea DOM 元素 innerHTML:', el.innerHTML);
              console.log('🔍 textarea DOM 元素 textContent:', el.textContent);
              console.log('🔍 textarea DOM 元素 innerText:', el.innerText);
              
              // 嘗試多種方法獲取內容
              textareaValue = el.textContent || el.innerText || el.innerHTML || '';
              
              // 如果內容為空，嘗試從組件的其他屬性獲取
              if (!textareaValue) {
                textareaValue = component.get('content') || component.get('innerHTML') || '';
                console.log('⚠️ 從組件屬性獲取 textarea 內容:', textareaValue);
              }
              
              // 如果仍然為空，嘗試從組件的子組件獲取
              if (!textareaValue) {
                try {
                  const components = component.get('components');
                  if (components && components.length > 0) {
                    // textarea 的內容通常存儲在子組件中
                    const firstChild = components[0];
                    if (firstChild && firstChild.get && typeof firstChild.get === 'function') {
                      textareaValue = firstChild.get('content') || '';
                      console.log('⚠️ 從子組件獲取 textarea 內容:', textareaValue);
                    }
                  }
                } catch (childError) {
                  console.warn('⚠️ 獲取子組件內容時出錯:', childError);
                }
              }
              
              console.log('✅ 最終獲取的 textarea 內容:', textareaValue);
            } else {
              console.warn('⚠️ textarea 組件沒有 DOM 元素');
            }
            
            // 如果 DOM 方法失敗，嘗試其他方法
            if (!textareaValue) {
              textareaValue = component.get('content') || component.get('innerHTML') || '';
              console.log('⚠️ 使用備用方法獲取 textarea 內容:', textareaValue);
            }
            
            // 最後的備用方案：檢查子組件
            if (!textareaValue) {
              try {
                const components = component.get('components');
                if (components && components.length > 0) {
                  const firstChild = components[0];
                  if (firstChild && firstChild.get && typeof firstChild.get === 'function') {
                    textareaValue = firstChild.get('content') || '';
                    console.log('⚠️ 最終備用方案：從子組件獲取 textarea 內容:', textareaValue);
                  }
                }
              } catch (finalChildError) {
                console.warn('⚠️ 最終備用方案獲取子組件內容時出錯:', finalChildError);
              }
            }
          } catch (error) {
            console.warn('⚠️ 獲取 textarea 內容時出錯:', error);
            textareaValue = component.get('content') || '';
          }
          
          const textareaData = {
            placeholder: attributes.placeholder || '',
            value: textareaValue,
            defaultValue: textareaValue, // 確保 defaultValue 和 value 一致
            name: attributes.name || '',
            required: attributes.required || false,
            disabled: attributes.disabled || false,
            readonly: attributes.readonly || false,
            rows: attributes.rows || '4',
            cols: attributes.cols || '50',
            maxlength: attributes.maxlength || '',
            minlength: attributes.minlength || ''
          };
          
          component.set('editFormData', textareaData);
          console.log('🔧 textarea 組件數據初始化完成:', textareaData);
          break;
          
        case 'select':
          const options = component.get('components');
          let selectOptions = [];
          if (options && options.length > 0) {
            selectOptions = options.map(option => {
              let text = '';
              let value = '';
              let selected = false;
              
              try {
                // 優先從 DOM 元素獲取文字內容，這是最可靠的方法
                const el = option.getEl ? option.getEl() : null;
                if (el) {
                  text = el.textContent || el.innerText || '';
                  console.log('✅ 從 DOM 獲取文字:', text);
                }
                
                // 如果 DOM 方法失敗，嘗試其他方法
                if (!text) {
                  text = option.get('content') || option.get('innerHTML') || option.get('text') || '';
                  console.log('⚠️ 使用備用方法獲取文字:', text);
                }
                
                // 獲取 value 和 selected 屬性
                value = option.getAttributes().value || '';
                selected = option.getAttributes().selected || false;
                
                // 如果文字為空但 value 有值，使用 value 作為文字
                if (!text && value) {
                  text = value;
                  console.log('🔄 使用 value 作為文字:', text);
                }
                
                console.log('🔍 Select option 解析結果:', {
                  value: value,
                  text: text.trim(),
                  selected: selected
                });
                
                return {
                  value: value,
                  text: text.trim(),
                  selected: selected
                };
              } catch (optionError) {
                console.warn('⚠️ 解析 select option 時出錯:', optionError);
                return {
                  value: '',
                  text: '選項',
                  selected: false
                };
              }
            });
          }
          
          component.set('editFormData', {
            name: attributes.name || '',
            required: attributes.required || false,
            disabled: attributes.disabled || false,
            multiple: attributes.multiple || false,
            size: attributes.size || '1',
            options: selectOptions
          });
          console.log('🔧 select 組件數據初始化完成，選項數量:', selectOptions.length);
          break;
          
        case 'button':
          component.set('editFormData', {
            type: attributes.type || 'button',
            value: component.get('content') || '',
            name: attributes.name || '',
            disabled: attributes.disabled || false,
            form: attributes.form || '',
            formaction: attributes.formaction || '',
            formmethod: attributes.formmethod || 'get',
            formnovalidate: attributes.formnovalidate || false
          });
          console.log('🔧 button 組件數據初始化完成');
          break;
          
        case 'label':
          component.set('editFormData', {
            for: attributes.for || '',
            value: component.get('content') || '',
            required: attributes.required || false
          });
          console.log('🔧 label 組件數據初始化完成');
          break;
          
        case 'td':
        case 'th':
          // 處理 table cell，允許編輯文字內容
          let cellContent = '';
          try {
            const el = component.getEl();
            if (el) {
              cellContent = el.textContent || el.innerText || el.innerHTML || '';
              console.log('✅ 從 DOM 獲取 table cell 內容:', cellContent);
            }
            
            if (!cellContent) {
              cellContent = component.get('content') || component.get('innerHTML') || '';
              console.log('⚠️ 使用備用方法獲取 table cell 內容:', cellContent);
            }
          } catch (error) {
            console.warn('⚠️ 獲取 table cell 內容時出錯:', error);
            cellContent = component.get('content') || '';
          }
          
          component.set('editFormData', {
            content: cellContent,
            tagName: tagName,
            ...attributes
          });
          console.log('🔧 table cell 組件數據初始化完成:', { tagName, content: cellContent });
          break;
          
        case 'img':
          // 獲取 src，如果是 base64 則清除
          let imgSrc = attributes.src || '';
          if (imgSrc && imgSrc.startsWith('data:image')) {
            console.warn('⚠️ img 組件初始化時檢測到 base64 格式，清除它:', imgSrc.substring(0, 50));
            imgSrc = ''; // 清除 base64，等待用戶上傳正確的 URL
          }
          
          // 從 DOM 元素獲取 src（最可靠）
          try {
            const el = component.getEl();
            if (el && el.src) {
              const domSrc = el.src;
              // 如果是完整的 URL（包含域名），提取相對路徑
              if (domSrc && !domSrc.startsWith('data:image')) {
                // 如果是絕對 URL，嘗試提取相對路徑
                if (domSrc.startsWith('http')) {
                  try {
                    const urlObj = new URL(domSrc);
                    const extractedPath = urlObj.pathname;
                    // 檢查提取的路徑是否合理（不應該是 /350x250 這樣的錯誤路徑）
                    if (extractedPath && extractedPath.length > 1 && !extractedPath.match(/^\/\d+x\d+$/)) {
                      imgSrc = extractedPath;
                      console.log('🔄 從 DOM 提取相對路徑:', imgSrc);
                    } else {
                      // 如果提取的路徑不合理，使用組件的 attributes.src
                      console.warn('⚠️ 從 DOM 提取的路徑不合理:', extractedPath, '，使用組件 attributes.src');
                      if (attributes.src && !attributes.src.includes('via.placeholder.com')) {
                        imgSrc = attributes.src;
                      }
                    }
                  } catch (urlError) {
                    console.warn('⚠️ 解析 URL 時出錯:', urlError);
                    // 如果解析失敗，使用組件的 attributes.src
                    if (attributes.src && !attributes.src.includes('via.placeholder.com')) {
                      imgSrc = attributes.src;
                    }
                  }
                } else {
                  // 相對路徑，直接使用
                  imgSrc = domSrc;
                }
              }
            }
          } catch (error) {
            console.warn('⚠️ 從 DOM 獲取 img src 時出錯:', error);
            // 如果出錯，使用組件的 attributes.src
            if (attributes.src && !attributes.src.includes('via.placeholder.com')) {
              imgSrc = attributes.src;
            }
          }
          
          // 如果最終的 imgSrc 還是 placeholder 或空，但 attributes.src 有有效值，使用 attributes.src
          if ((!imgSrc || imgSrc.includes('via.placeholder.com')) && attributes.src && !attributes.src.includes('via.placeholder.com')) {
            imgSrc = attributes.src;
            console.log('🔄 使用組件 attributes.src:', imgSrc);
          }
          
          // 如果 src 仍然是空的或是 placeholder，使用默認的 no-image.jpg
          // 注意：只在初始化時使用 no-image.jpg，不在保存時覆蓋已上傳的圖片
          if (!imgSrc || imgSrc.includes('via.placeholder.com')) {
            // 檢查是否已有有效的 editFormData（避免在用戶上傳圖片後被重置）
            const existingEditFormData = component.get('editFormData');
            if (!existingEditFormData || !existingEditFormData.src || 
                existingEditFormData.src.includes('via.placeholder.com') || 
                existingEditFormData.src.includes('/assets/no-image.jpg')) {
              imgSrc = '/assets/no-image.jpg';
              console.log('🖼️ 使用默認 no-image.jpg 作為占位符');
              // 同時更新組件屬性和 DOM 元素
              component.setAttributes({ src: imgSrc });
              const el = component.getEl();
              if (el) {
                el.src = imgSrc;
              }
            } else {
              // 保留 editFormData 中的有效 src
              imgSrc = existingEditFormData.src;
              console.log('🔄 保留 editFormData 中的有效 src:', imgSrc);
            }
          }
          
          // 讀取 width 和 height - 優先從屬性讀取，如果沒有則從 CSS 樣式中讀取
          let imgWidth = attributes.width || '';
          let imgHeight = attributes.height || '';
          const styleStr = attributes.style || '';
          
          // 解析 CSS 樣式中的 width 和 height
          if (styleStr) {
            const styleObj = {};
            styleStr.split(';').forEach(rule => {
              const [key, value] = rule.split(':').map(s => s.trim());
              if (key && value) {
                styleObj[key] = value;
              }
            });
            
            // 如果屬性中沒有 width/height，但 CSS 樣式中有，則從 CSS 讀取
            if (!imgWidth && styleObj.width) {
              imgWidth = styleObj.width;
              console.log('🔄 img 初始化: 從 CSS 樣式讀取 width:', imgWidth);
            }
            if (!imgHeight && styleObj.height && styleObj.height !== 'auto') {
              imgHeight = styleObj.height;
              console.log('🔄 img 初始化: 從 CSS 樣式讀取 height:', imgHeight);
            }
          }
          
          component.set('editFormData', {
            src: imgSrc,
            alt: attributes.alt || '',
            width: imgWidth,
            height: imgHeight,
            style: styleStr
          });
          console.log('🔧 img 組件數據初始化完成:', {
            src: imgSrc,
            alt: attributes.alt,
            width: imgWidth,
            height: imgHeight
          });
          break;
          
        default:
          component.set('editFormData', {
            tagName: tagName,
            content: component.get('content') || '',
            ...attributes
          });
          console.log('🔧 默認組件數據初始化完成:', tagName);
          break;
      }
    } catch (error) {
      console.error('❌ initializeFormData 發生錯誤:', error);
      console.error('❌ 組件狀態:', {
        hasGet: !!component.get,
        getType: typeof component.get,
        component: component
      });
    }
  };

  // 移除所有 modal 相關函數
  // const createEditModal = () => { ... } - 已移除
  // const showEditModal = () => { ... } - 已移除
  // const hideEditModal = () => { ... } - 已移除
  // const renderEditForm = () => { ... } - 已移除

  // 移除所有 update*Component 函數，因為這些邏輯將在右側面板中處理
  // const updateInputComponent = async () => { ... } - 已移除
  // const updateTextareaComponent = async () => { ... } - 已移除
  // const updateSelectComponent = async () => { ... } - 已移除
  // const updateButtonComponent = async () => { ... } - 已移除
  // const updateLabelComponent = async () => { ... } - 已移除

  // 移除 handleFormChange 和 handleSave 函數，因為這些邏輯將在右側面板中處理
  // const handleFormChange = (field, value) => { ... } - 已移除
  // const handleSave = async () => { ... } - 已移除

  // 使用 GrapesJS 官方方法添加編輯按鈕到組件工具欄
  const addEditButtonToToolbar = (component) => {
    // 添加安全檢查
    if (!component || !component.get || typeof component.get !== 'function') {
      console.warn('⚠️ addEditButtonToToolbar: 組件無效或已被銷毀');
      return;
    }
    
    try {
      // 獲取組件的工具欄
      const toolbar = component.get('toolbar');
      
      const tagName = component.get('tagName');
      
      // 檢查是否已經有編輯按鈕（或 table 的配置按鈕）
      const hasEditBtn = toolbar && toolbar.some(item => item.command === 'edit-component');
      const hasConfigBtn = toolbar && toolbar.some(item => item.command === 'configure-table');
      
      if (hasEditBtn && (tagName?.toLowerCase() !== 'table' || hasConfigBtn)) {
        return;
      }
      
      // 創建編輯按鈕配置
      const editButton = {
        attributes: { class: 'gjs-edit-btn-toolbar' },
        command: 'edit-component',
        label: `<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
        </svg>`,
        title: resources.eformDesigner?.editComponent || '編輯組件',
        // 使用白色圖標，與其他工具欄按鈕保持一致
        style: {
          color: '#ffffff', // 改為白色圖標
          fontSize: '14px',
          padding: '4px',
          cursor: 'pointer',
          backgroundColor: '#1890ff', // 藍色背景
          border: 'none',
          borderRadius: '4px',
          width: '28px',
          height: '28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: '0.9',
          transition: 'opacity 0.2s ease'
        }
      };
      
      // 為 table 組件添加配置按鈕；為 td/th 添加「選中整個表格」按鈕
      const toolbarButtons = [editButton];
      const tagLower = tagName ? tagName.toLowerCase() : '';
      if (tagLower === 'td' || tagLower === 'th') {
        const selectTableBtn = {
          attributes: { class: 'gjs-select-parent-table-btn-toolbar' },
          command: 'select-parent-table',
          label: `<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M10,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V8C22,6.89 21.1,6 20,6H12L10,4Z"/></svg>`,
          title: resources.eformDesigner?.grapesJs?.selectParentTable || '選中整個表格',
          style: {
            color: '#ffffff',
            fontSize: '14px',
            padding: '4px',
            cursor: 'pointer',
            backgroundColor: '#722ed1',
            border: 'none',
            borderRadius: '4px',
            width: '28px',
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: '0.9',
            transition: 'opacity 0.2s ease',
            marginLeft: '4px'
          }
        };
        toolbarButtons.unshift(selectTableBtn);
      } else if (tagLower === 'table') {
        const configButton = {
          attributes: { class: 'gjs-table-config-btn-toolbar' },
          command: 'configure-table',
          label: `<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>
          </svg>`,
          title: resources.eformDesigner?.grapesJs?.tableConfig || '表格設置',
          style: {
            color: '#ffffff',
            fontSize: '14px',
            padding: '4px',
            cursor: 'pointer',
            backgroundColor: '#52c41a', // 綠色背景，區分於編輯按鈕
            border: 'none',
            borderRadius: '4px',
            width: '28px',
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: '0.9',
            transition: 'opacity 0.2s ease',
            marginLeft: '4px'
          }
        };
        toolbarButtons.push(configButton);
      }

      // 將按鈕添加到組件工具欄
      if (toolbar) {
        toolbar.push(...toolbarButtons);
      } else {
        component.set('toolbar', toolbarButtons);
      }
      
      const componentId = component.getId();
      console.log('✅ 按鈕已添加到組件工具欄:', tagName, componentId, tagName?.toLowerCase() === 'table' ? '(包含配置按鈕)' : '');
    } catch (error) {
      console.error('❌ addEditButtonToToolbar 發生錯誤:', error);
      console.error('❌ 組件狀態:', {
        hasGet: !!component.get,
        getType: typeof component.get,
        component: component
      });
    }
  };

  // 為所有現有組件添加編輯按鈕到工具欄
  const addEditButtonsToAllComponentsToolbar = () => {
    try {
      const allComponents = editor.DomComponents.getComponents();
      let validComponents = 0;
      let invalidComponents = 0;
      
      allComponents.forEach(component => {
        // 添加安全檢查
        if (component && component.get && typeof component.get === 'function') {
          addEditButtonToToolbar(component);
          validComponents++;
        } else {
          console.warn('⚠️ 發現無效組件，跳過添加編輯按鈕:', component);
          invalidComponents++;
        }
      });
      
      console.log('✅ 已為所有現有組件添加編輯按鈕到工具欄');
      console.log(`📊 有效組件: ${validComponents}, 無效組件: ${invalidComponents}`);
    } catch (error) {
      console.error('❌ addEditButtonsToAllComponentsToolbar 發生錯誤:', error);
    }
  };

  // 攔截圖片組件的雙擊事件，禁用內建的 assetManager，改用我們的編輯界面
  editor.on('component:dblclick', function(component) {
    // 添加安全檢查
    if (!component || !component.get || typeof component.get !== 'function') {
      console.warn('⚠️ component:dblclick: 組件無效或已被銷毀');
      return;
    }
    
    try {
      const tagName = component.get('tagName');
      
      // 如果是圖片組件，阻止默認行為（內建的 assetManager），改用我們的編輯界面
      if (tagName && tagName.toLowerCase() === 'img') {
        console.log('🖼️ 攔截圖片組件雙擊事件，改用自定義編輯界面');
        
        // 觸發自定義編輯事件，與 edit 按鈕行為一致
        editor.trigger('edit-component-requested', component);
        
        // 阻止默認的 assetManager 行為
        return false;
      }
    } catch (error) {
      console.error('❌ component:dblclick 事件處理錯誤:', error);
    }
  });

  // 依 tagName 啟用 GrapesJS 官方 resizable 拖曳縮放（不依賴內建型別名稱，最可靠）。
  // 表格 cell 的內建型別名是 'cell' 而非 'td/th'，用 tagName 判斷才穩定。
  const applyResizableByTag = (component, rawTag) => {
    try {
      if (!component || typeof component.set !== 'function') return;
      const tag = (rawTag || component.get('tagName') || '').toLowerCase();
      if (!tag) return;

      // 儲存格：只給右側（欄寬）＋底部（列高）把手，避免破壞表格版面
      const cellResizable = {
        tl: false, tc: false, tr: false,
        cl: false, cr: true,
        bl: false, bc: true, br: false,
        minDim: 20, keyWidth: 'width', keyHeight: 'height',
        currentUnit: true, unitWidth: 'px', unitHeight: 'px'
      };
      // 表格整體：右／下／右下角
      const tableResizable = {
        tl: false, tc: false, tr: false,
        cl: false, cr: true,
        bl: false, bc: true, br: true,
        minDim: 30, keyWidth: 'width', keyHeight: 'height',
        currentUnit: true, unitWidth: 'px', unitHeight: 'px'
      };
      // 一般容器：8 方向全開
      const boxResizable = {
        tl: true, tc: true, tr: true,
        cl: true, cr: true,
        bl: true, bc: true, br: true,
        minDim: 20
      };

      let opts = null;
      if (tag === 'td' || tag === 'th') opts = cellResizable;
      else if (tag === 'table') opts = tableResizable;
      else if (['div', 'section', 'article', 'aside', 'header', 'footer'].includes(tag)) opts = boxResizable;

      if (opts) {
        component.set('resizable', opts);
        console.log(`📐 [resizable v2] 已為 <${tag}> 啟用拖曳縮放把手`);
      }
    } catch (e) {
      console.warn('⚠️ applyResizableByTag 失敗:', e);
    }
  };

  // 元件被加入時（含載入既有 HTML）即先設定 resizable，避免選中時機問題
  editor.on('component:create', (component) => {
    applyResizableByTag(component, null);
  });

  // 監聽組件選中事件
  editor.on('component:selected', function(component) {
    // 添加安全檢查
    if (!component || !component.get || typeof component.get !== 'function') {
      console.warn('⚠️ component:selected: 組件無效或已被銷毀');
      return;
    }
    
    try {
      const tagName = component.get('tagName');
      const componentId = component.getId();
      console.log(' 組件被選中:', tagName, componentId);

      // 選中時再保險設定一次（涵蓋 component:create 之前未套用的情況）
      applyResizableByTag(component, tagName);
      
      // 為選中的組件添加編輯按鈕到工具欄（如果還沒有的話）
      addEditButtonToToolbar(component);
      
      // 如果是 textarea 組件，確保內容正確顯示
      if (tagName.toLowerCase() === 'textarea') {
        // 確保 editFormData 已初始化
        if (!component.get('editFormData')) {
          console.log('🔧 textarea 組件選中，初始化 editFormData');
          initializeFormData(component);
        }
        
        const content = component.get('content');
        const el = component.getEl();
        if (el) {
          console.log('🔍 textarea 組件選中，檢查內容:', {
            componentContent: content,
            domContent: el.textContent,
            attributes: component.getAttributes(),
            editFormData: component.get('editFormData')
          });
          
          // 如果 DOM 內容與組件內容不一致，將 DOM 內容同步到組件
          if (content !== el.textContent) {
            console.log('🔄 同步 textarea 內容，DOM 與組件不一致');
            const domContent = el.textContent || '';
            
            // 將 DOM 內容同步到組件的 content 屬性
            component.set('content', domContent);
            
            // 同時更新其他相關屬性
            try {
              component.set('innerHTML', domContent);
              component.set('textContent', domContent);
              console.log('✅ textarea 組件內容屬性已同步到 DOM 內容:', domContent);
            } catch (error) {
              console.warn('⚠️ 同步組件內容屬性時出錯:', error);
            }
            
            // 更新 editFormData 以保持一致性
            const currentEditFormData = component.get('editFormData') || {};
            currentEditFormData.defaultValue = domContent;
            currentEditFormData.value = domContent;
            component.set('editFormData', currentEditFormData);
            console.log('✅ textarea editFormData 已同步到 DOM 內容:', domContent);
          }
        }
      }
    } catch (error) {
      console.error('❌ component:selected 事件處理錯誤:', error);
      console.error('❌ 組件狀態:', {
        hasGet: !!component.get,
        getType: typeof component.get,
        component: component
      });
    }
  });

  // 監聽組件取消選中事件
  editor.on('component:deselected', function(component) {
    // 添加安全檢查
    if (!component || !component.get || typeof component.get !== 'function') {
      console.warn('⚠️ component:deselected: 組件無效或已被銷毀');
      return;
    }
    
    try {
      const tagName = component.get('tagName');
      const componentId = component.getId();
      console.log(' 組件取消選中:', tagName, componentId);
      
      // 工具欄按鈕會自動隱藏，不需要額外處理
    } catch (error) {
      console.error('❌ component:deselected 事件處理錯誤:', error);
      console.error('❌ 組件狀態:', {
        hasGet: !!component.get,
        getType: typeof component.get,
        component: component
      });
    }
  });

  // 監聽新組件添加事件
  editor.on('component:add', function(component) {
    // 添加安全檢查
    if (!component || !component.get || typeof component.get !== 'function') {
      console.warn('⚠️ component:add: 組件無效或已被銷毀');
      return;
    }
    
    try {
      console.log(' 新組件被添加:', component.get('tagName'), component.getId());
      
      // 如果是日期輸入框，確保設置默認的日期格式（含 type="date" 與 data-input-type="date"）
      const attrs = component.getAttributes();
      if (component.get('tagName').toLowerCase() === 'input' && 
          (attrs.type === 'date' || attrs['data-input-type'] === 'date')) {
        const currentFormat = attrs['data-date-format'];
        if (!currentFormat) {
          console.log('📅 為新添加的日期輸入框設置默認格式');
          component.setAttributes({ 'data-date-format': 'YYYY-MM-DD' });
        }
      }
      
      // 如果是 textarea，確保內容正確顯示
      if (component.get('tagName').toLowerCase() === 'textarea') {
        // 初始化 editFormData
        console.log('🔧 新添加的 textarea 組件，初始化 editFormData');
        initializeFormData(component);
        
        const content = component.get('content');
        const el = component.getEl();
        
        // 即使沒有內容也要同步，確保 DOM 和組件狀態一致
        if (el) {
          console.log('🔄 新添加的 textarea 組件，同步內容:', {
            componentContent: content,
            domContent: el.textContent
          });
          
          // 如果內容不一致，強制同步
          if (content !== el.textContent) {
            el.textContent = content || '';
            console.log('✅ textarea DOM 內容已同步:', content || '');
            
            // 同時更新 editFormData 以保持一致性
            const currentEditFormData = component.get('editFormData') || {};
            currentEditFormData.defaultValue = content || '';
            currentEditFormData.value = content || '';
            component.set('editFormData', currentEditFormData);
            console.log('✅ textarea editFormData 已同步:', currentEditFormData);
          }
        }
      }
      
      // 如果是 img，確保 src 正確保留，不要重置為默認值
      if (component.get('tagName').toLowerCase() === 'img') {
        console.log('🖼️ 新添加的 img 組件，檢查 src');
        const attributes = component.getAttributes();
        const currentSrc = attributes.src || '';
        const el = component.getEl();
        
        // 確保 DOM 元素存在並正確顯示
        if (el) {
          console.log('✅ img DOM 元素存在:', {
            src: el.src,
            alt: el.alt,
            width: el.width,
            height: el.height,
            style: el.style.cssText
          });
          
          // 確保圖片可見
          el.style.display = '';
          el.style.visibility = 'visible';
          el.style.opacity = '1';
        } else {
          console.warn('⚠️ img DOM 元素不存在，等待渲染...');
        }
        
        // 檢查是否已有有效的 editFormData（可能是在保存後觸發的 component:add）
        const existingEditFormData = component.get('editFormData');
        if (existingEditFormData && existingEditFormData.src && !existingEditFormData.src.includes('via.placeholder.com')) {
          console.log('✅ img 組件已有有效的 editFormData，保留現有數據:', existingEditFormData.src);
          // 確保組件屬性和 DOM 同步
          if (existingEditFormData.src !== currentSrc) {
            component.setAttributes({ src: existingEditFormData.src });
            if (el) {
              el.src = existingEditFormData.src.startsWith('/') 
                ? `${window.location.origin}${existingEditFormData.src}`
                : existingEditFormData.src;
            }
            console.log('🔄 已同步組件屬性和 DOM');
          }
          return; // 不要重新初始化
        }
        
        // 從 DOM 元素獲取實際的 src（最可靠）
        let domSrc = '';
        if (el && el.src) {
          domSrc = el.src;
          // 如果是完整的 URL（包含域名），提取相對路徑
          if (domSrc && !domSrc.startsWith('data:image')) {
            if (domSrc.startsWith('http')) {
              try {
                const urlObj = new URL(domSrc);
                domSrc = urlObj.pathname;
                console.log('🔄 從 DOM 提取相對路徑:', domSrc);
              } catch (e) {
                console.warn('⚠️ 解析 URL 時出錯:', e);
              }
            }
          }
        }
        
        // 如果當前 src 是默認的 placeholder，但 DOM 中有不同的 src，保留 DOM 的 src
        if (currentSrc && currentSrc.includes('via.placeholder.com') && domSrc && !domSrc.includes('via.placeholder.com')) {
          console.log('🔄 檢測到默認 placeholder，但 DOM 中有實際圖片 URL，保留 DOM 的 src');
          component.setAttributes({ src: domSrc });
          console.log('✅ img src 已更新為 DOM 的值:', domSrc);
        }
        
        // 如果 src 為 base64，清除它並使用 no-image.jpg
        if (currentSrc && currentSrc.startsWith('data:image')) {
          console.warn('⚠️ 新添加的 img 組件檢測到 base64 格式，清除它');
          component.setAttributes({ src: domSrc || '/assets/no-image.jpg' });
        }
        
        // 如果 src 為空或是 placeholder，且不是已上傳的圖片，使用默認的 no-image.jpg
        if (!currentSrc || currentSrc.includes('via.placeholder.com')) {
          // 檢查 editFormData 中是否有有效的已上傳圖片 URL
          const existingEditFormData = component.get('editFormData');
          const hasValidUploadedSrc = existingEditFormData && 
                                      existingEditFormData.src && 
                                      !existingEditFormData.src.includes('via.placeholder.com') &&
                                      !existingEditFormData.src.includes('/assets/no-image.jpg') &&
                                      !existingEditFormData.src.startsWith('data:image');
          
          if (!hasValidUploadedSrc) {
            // 只有在沒有已上傳圖片時，才使用 no-image.jpg
            const defaultSrc = '/assets/no-image.jpg';
            if (currentSrc !== defaultSrc) {
              console.log('🖼️ 新添加的 img 組件 src 為空或 placeholder，使用默認 no-image.jpg');
              component.setAttributes({ src: defaultSrc });
              if (el) {
                el.src = defaultSrc;
              }
            }
          } else {
            // 如果 editFormData 中有有效的已上傳圖片 URL，使用它
            console.log('🖼️ 保留 editFormData 中的已上傳圖片:', existingEditFormData.src);
            component.setAttributes({ src: existingEditFormData.src });
            if (el) {
              el.src = existingEditFormData.src.startsWith('/') 
                ? `${window.location.origin}${existingEditFormData.src}`
                : existingEditFormData.src;
            }
          }
        }
        
        // 初始化 editFormData（只有在沒有有效的 editFormData 時才初始化）
        console.log('🔧 新添加的 img 組件，初始化 editFormData');
        initializeFormData(component);
        
        // 自動選中新添加的圖片組件，並打開右側編輯面板
        setTimeout(() => {
          try {
            if (editor && !editor.isDestroyed && component) {
              // 確保組件仍然有效
              if (component.get && typeof component.get === 'function') {
                // 選中組件
                editor.select(component);
                console.log('✅ 自動選中新添加的 img 組件:', component.getId());
                
                // 觸發編輯請求，打開右側面板
                setTimeout(() => {
                  const currentComponent = editor.getSelected();
                  if (currentComponent && currentComponent.getId() === component.getId()) {
                    editor.trigger('edit-component-requested', currentComponent);
                    console.log('✅ 自動打開右側編輯面板');
                  } else {
                    console.warn('⚠️ 組件未被選中，無法打開右側面板');
                  }
                }, 100);
              }
            }
          } catch (error) {
            console.warn('⚠️ 自動選中 img 組件時出錯:', error);
          }
        }, 200); // 延遲確保組件完全初始化和渲染
      }
      
      // 如果是 table cell (td/th)，確保初始化 editFormData
      if (component.get('tagName')?.toLowerCase() === 'td' || component.get('tagName')?.toLowerCase() === 'th') {
        console.log('🔧 新添加的 table cell 組件，初始化 editFormData');
        initializeFormData(component);
        
        // 確保 editFormData.content 與實際內容一致
        const el = component.getEl();
        if (el) {
          const cellContent = el.textContent || el.innerText || el.innerHTML || '';
          const currentEditFormData = component.get('editFormData') || {};
          if (currentEditFormData.content !== cellContent) {
            currentEditFormData.content = cellContent;
            component.set('editFormData', currentEditFormData);
            console.log('✅ table cell editFormData.content 已同步:', cellContent);
          }
        }
      }
      
      // 為新添加的組件添加編輯按鈕到工具欄
      setTimeout(() => {
        // 在延遲後再次檢查組件是否仍然有效
        if (component && component.get && typeof component.get === 'function') {
          addEditButtonToToolbar(component);
        } else {
          console.warn('⚠️ component:add setTimeout: 組件在延遲期間變為無效');
        }
      }, 100); // 稍微延遲確保組件完全初始化
    } catch (error) {
      console.error('❌ component:add 事件處理錯誤:', error);
      console.error('❌ 組件狀態:', {
        hasGet: !!component.get,
        getType: typeof component.get,
        component: component
      });
    }
  });

  // 同步 img 組件尺寸的函數（可重用）
  const syncImgDimensions = (component, eventName = 'unknown') => {
    if (!component || component.get('tagName').toLowerCase() !== 'img') {
      return;
    }
    
    try {
      const el = component.getEl();
      if (!el) {
        return;
      }
      
      // 從 DOM 元素讀取實際的計算樣式和尺寸
      const computedStyle = window.getComputedStyle(el);
      const domWidth = computedStyle.width || '';
      const domHeight = computedStyle.height || '';
      const offsetWidth = el.offsetWidth;
      const offsetHeight = el.offsetHeight;
      
      console.log(`🔍 [${eventName}] img 組件尺寸檢查:`, {
        domWidth,
        domHeight,
        offsetWidth,
        offsetHeight,
        style: computedStyle.width + ' x ' + computedStyle.height
      });
      
      // 將像素值轉換為字符串格式
      const normalizeSize = (size) => {
        if (!size) return '';
        const num = parseFloat(size);
        if (!isNaN(num)) {
          return `${num}px`;
        }
        return size;
      };
      
      // 優先使用 offsetWidth/offsetHeight（實際渲染尺寸），其次使用計算樣式
      const targetWidth = offsetWidth ? `${offsetWidth}px` : normalizeSize(domWidth);
      const targetHeight = offsetHeight ? `${offsetHeight}px` : normalizeSize(domHeight);
      
      const attrs = component.getAttributes();
      const currentWidth = attrs.width || '';
      const currentHeight = attrs.height || '';
      
      let needsUpdate = false;
      const newAttrs = {};
      
      if (targetWidth && targetWidth !== normalizeSize(currentWidth)) {
        newAttrs.width = targetWidth;
        needsUpdate = true;
        console.log(`🔄 [${eventName}] img 同步 width:`, { targetWidth, currentWidth });
      }
      
      if (targetHeight && targetHeight !== 'auto' && targetHeight !== normalizeSize(currentHeight)) {
        newAttrs.height = targetHeight;
        needsUpdate = true;
        console.log(`🔄 [${eventName}] img 同步 height:`, { targetHeight, currentHeight });
      }
      
      if (needsUpdate) {
        // 在更新屬性之前，先從多個來源讀取並保存 src 和 alt
        const currentAttrs = component.getAttributes();
        const el = component.getEl();
        const existingEditFormData = component.get('editFormData') || {};
        
        // 優先順序：組件屬性 > editFormData > DOM 元素
        // 🔧 關鍵：用戶剛上傳新圖片時，handleFormChange 會先 setAttributes 更新組件屬性，
        // 此時 component:styleUpdate 可能早於 editFormData 更新而觸發，故優先使用 currentAttrs.src，
        // 避免用舊的 editFormData.src 覆蓋新上傳的圖片
        let srcToPreserve = '';
        let altToPreserve = '';
        
        const toRelPath = (s) => {
          if (!s || typeof s !== 'string' || !s.trim()) return '';
          let t = s.trim();
          if (t.startsWith('http')) {
            try { t = new URL(t).pathname; } catch (e) {}
          }
          return t;
        };
        const isValid = (s) => s && !s.includes('via.placeholder.com') && !s.includes('/assets/no-image.jpg') && !s.startsWith('data:image');
        
        // 1. 優先從組件屬性獲取（上傳時 setAttributes 先於 editFormData 更新）
        if (isValid(currentAttrs.src)) {
          srcToPreserve = toRelPath(currentAttrs.src);
        }
        // 2. 若組件屬性無效，從 editFormData 獲取
        if (!srcToPreserve && isValid(existingEditFormData?.src)) {
          srcToPreserve = toRelPath(existingEditFormData.src);
        }
        
        if (existingEditFormData && existingEditFormData.alt && 
            typeof existingEditFormData.alt === 'string' && 
            existingEditFormData.alt.trim()) {
          altToPreserve = existingEditFormData.alt.trim();
        }
        if (!altToPreserve && currentAttrs.alt && typeof currentAttrs.alt === 'string') {
          altToPreserve = currentAttrs.alt.trim();
        }
        
        // 3. 如果組件屬性中也沒有，從 DOM 元素讀取（但只有在 editFormData 和組件屬性都沒有時才使用）
        // 🔧 關鍵：不要用 DOM src 覆蓋 editFormData.src
        if (!srcToPreserve && el) {
          const domSrc = el.getAttribute('src') || el.src || '';
          if (domSrc && typeof domSrc === 'string' && 
              domSrc.trim() && 
              !domSrc.startsWith('data:image') &&
              !domSrc.includes('via.placeholder.com') &&
              !domSrc.includes('/assets/no-image.jpg')) {
            srcToPreserve = domSrc.trim();
            // 如果是絕對 URL，提取相對路徑
            if (srcToPreserve.startsWith('http')) {
              try {
                const urlObj = new URL(srcToPreserve);
                srcToPreserve = urlObj.pathname;
              } catch (e) {
                // 如果解析失敗，保留原值
              }
            }
          }
        }
        
        if (!altToPreserve && el) {
          const domAlt = el.getAttribute('alt') || el.alt || '';
          if (domAlt && typeof domAlt === 'string') {
            altToPreserve = domAlt.trim();
          }
        }
        
        console.log(`🔍 [${eventName}] 保留的屬性:`, {
          srcToPreserve,
          altToPreserve,
          existingSrc: existingEditFormData.src,
          attrsSrc: currentAttrs.src,
          domSrc: el ? (el.getAttribute('src') || el.src) : 'no el'
        });
        
        // 更新組件屬性 - 明確設置 src 和 alt，確保不會丟失
        const attrsToUpdate = {
          ...newAttrs, // 新的尺寸
        };
        // 只在有值時才更新 src 和 alt，避免覆蓋有效值
        if (srcToPreserve) {
          attrsToUpdate.src = srcToPreserve;
        }
        if (altToPreserve) {
          attrsToUpdate.alt = altToPreserve;
        }
        component.setAttributes(attrsToUpdate, { silent: true });
        
        // 🔧 同步更新 DOM 元素的 src，確保視覺顯示正確
        if (srcToPreserve && el) {
          const absoluteSrc = srcToPreserve.startsWith('/')
            ? `${window.location.origin}${srcToPreserve}`
            : srcToPreserve;
          el.src = absoluteSrc;
          el.setAttribute('src', srcToPreserve);
          console.log(`🔄 [${eventName}] 已同步 DOM 元素的 src: ${srcToPreserve}`);
        }
        if (altToPreserve && el) {
          el.setAttribute('alt', altToPreserve);
          el.alt = altToPreserve;
        }
        
        // 同步更新 editFormData - 確保所有屬性都被保留
        const editFormData = {
          ...existingEditFormData, // 保留所有原有屬性
          width: newAttrs.width || existingEditFormData.width || currentAttrs.width || '', // 更新尺寸
          height: newAttrs.height || existingEditFormData.height || currentAttrs.height || '' // 更新尺寸
        };
        // 只在有值時才設置 src 和 alt
        if (srcToPreserve) {
          editFormData.src = srcToPreserve;
        }
        if (altToPreserve) {
          editFormData.alt = altToPreserve;
        }
        component.set('editFormData', editFormData);
        
        console.log(`✅ [${eventName}] img 組件尺寸已同步，保留原有屬性:`, {
          width: editFormData.width,
          height: editFormData.height,
          src: editFormData.src,
          alt: editFormData.alt
        });
        
        // 觸發自定義事件通知外部組件更新
        setTimeout(() => {
          if (editor && !editor.isDestroyed) {
            editor.trigger('component:editFormDataUpdated', component);
          }
        }, 10);
      }
    } catch (error) {
      console.warn(`⚠️ [${eventName}] 同步 img 組件尺寸時出錯:`, error);
    }
  };

  // 監聽組件更新事件
  editor.on('component:update', function(component) {
    // 添加安全檢查
    if (!component || !component.get || typeof component.get !== 'function') {
      console.warn('⚠️ component:update: 組件無效或已被銷毀');
      return;
    }
    
    try {
      console.log(' 組件被更新:', component.get('tagName'), component.getId());
      
      // 確保編輯按鈕仍然存在於工具欄
      const toolbar = component.get('toolbar');
      if (toolbar && !toolbar.some(item => item.command === 'edit-component')) {
        addEditButtonToToolbar(component);
      }
      
      // 如果是日期輸入框，更新格式顯示（含 type="date" 與 data-input-type="date"）
      const compAttrs = component.getAttributes();
      if (component.get('tagName').toLowerCase() === 'input' && 
          (compAttrs.type === 'date' || compAttrs['data-input-type'] === 'date')) {
        const dateFormat = compAttrs['data-date-format'] || 'YYYY-MM-DD';
        console.log('📅 日期輸入框格式已更新:', dateFormat);
      }
      
      // 如果是 img 組件，同步 CSS 樣式中的 width/height 到屬性（拖拽調整大小時）
      if (component.get('tagName').toLowerCase() === 'img') {
        // 使用統一的同步函數（定義在下面）
        syncImgDimensions(component, 'component:update');
      }
      
      // 移除自動刷新，避免無限循環
      // setTimeout(() => {
      //   if (editor && !editor.isDestroyed && editor.refresh) {
      //     editor.refresh();
      //   }
      // }, 50);
    } catch (error) {
      console.error('❌ component:update 事件處理錯誤:', error);
      console.error('❌ 組件狀態:', {
        hasGet: !!component.get,
        getType: typeof component.get,
        component: component
      });
    }
  });

  // 監聽組件樣式變化事件（拖拽調整大小時可能會觸發）
  editor.on('component:styleUpdate', function(component) {
    syncImgDimensions(component, 'styleUpdate');
  });

  // 監聽組件屬性變化事件（當 style 屬性變化時）
  editor.on('component:change:style', function(component) {
    syncImgDimensions(component, 'change:style');
  });

  // 監聽 Canvas 的 mouseup 事件（拖拽結束時）
  const canvasEl = editor.Canvas.getFrameEl();
  if (canvasEl && canvasEl.contentDocument) {
    const canvasDoc = canvasEl.contentDocument;
    canvasDoc.addEventListener('mouseup', function(e) {
      // 延遲一點時間，確保 GrapesJS 已經更新了組件
      setTimeout(() => {
        const selected = editor.getSelected();
        if (selected) {
          syncImgDimensions(selected, 'mouseup');
        }
      }, 100);
    });
  }

  // 監聽編輯命令 - 簡化為只觸發事件
  editor.Commands.add('edit-component', {
    run: function(editor, sender, options) {
      console.log('🔧 編輯命令被觸發');
      console.log(' 發送者:', sender);
      console.log(' 選項:', options);
      
      const component = options.component || editor.getSelected();
      
      // 添加安全檢查，確保組件存在且有效
      if (!component) {
        console.warn('⚠️ 沒有選中的組件，無法編輯');
        return;
      }
      
      // 檢查組件是否仍然有效
      if (!component.get || typeof component.get !== 'function') {
        console.warn('⚠️ 組件無效或已被銷毀，無法編輯');
        return;
      }
      
      try {
        const tagName = component.get('tagName');
        const componentId = component.getId();
        
        if (!tagName) {
          console.warn('⚠️ 組件標籤名稱無效，無法編輯');
          return;
        }
        
        console.log(' 開始編輯組件:', tagName, 'ID:', componentId);
        
        // 初始化表單數據並存儲到組件
        initializeFormData(component);
        
        // 如果是 textarea 組件，記錄詳細信息
        if (tagName.toLowerCase() === 'textarea') {
          const editFormData = component.get('editFormData');
          console.log('🔧 textarea 編輯數據初始化完成:', editFormData);
        }
        
        // 只觸發自定義事件供 EFormDesigner 監聽，不創建 modal
        editor.trigger('edit-component-requested', component);
      } catch (error) {
        console.error('❌ 編輯組件時發生錯誤:', error);
        console.error('❌ 組件狀態:', {
          hasGet: !!component.get,
          getType: typeof component.get,
          component: component
        });
      }
    }
  });

  // 添加日期格式更新命令
  editor.Commands.add('update-date-format', {
    run: function(editor, sender, options) {
      const { component, newFormat } = options;
      
      // 添加安全檢查
      if (!component || !component.get || typeof component.get !== 'function') {
        console.warn('⚠️ update-date-format: 組件無效或已被銷毀');
        return;
      }
      
      if (!newFormat) {
        console.warn('⚠️ update-date-format: 新格式未提供');
        return;
      }
      
      try {
        console.log('📅 更新日期格式:', newFormat);
        component.setAttributes({ 'data-date-format': newFormat });
        
        // 觸發自定義事件
        editor.trigger('date-format-updated', { component, format: newFormat });
      } catch (error) {
        console.error('❌ update-date-format 命令執行錯誤:', error);
        console.error('❌ 組件狀態:', {
          hasGet: !!component.get,
          getType: typeof component.get,
          component: component
        });
      }
    }
  });

  // 為所有現有組件添加編輯按鈕到工具欄
  setTimeout(() => {
    try {
      // 檢查編輯器是否仍然有效
      if (editor && typeof editor.DomComponents === 'object') {
        addEditButtonsToAllComponentsToolbar();
      } else {
        console.warn('⚠️ 編輯器在延遲期間變為無效，跳過添加編輯按鈕');
      }
    } catch (error) {
      console.error('❌ 延遲添加編輯按鈕時發生錯誤:', error);
    }
  }, 500); // 延遲確保編輯器完全初始化
  
  console.log('✅ 編輯功能已設置（僅右側面板，無 modal）');

  // 在 addEditFunctionality 函數中添加對 change:content 事件的監聽
  editor.on('component:change:content', function(component) {
    // 添加安全檢查
    if (!component || !component.get || typeof component.get !== 'function') {
      console.warn('⚠️ component:change:content: 組件無效或已被銷毀');
      return;
    }
    
    try {
      const tagName = component.get('tagName');
      const componentId = component.getId();
      console.log(' 組件內容變化:', tagName, componentId);
      
      // 如果是 textarea 組件，確保 DOM 同步
      if (tagName.toLowerCase() === 'textarea') {
        // 修復：從組件獲取 content 值
        const content = component.get('content');
        const el = component.getEl();
        if (el) {
          console.log('🔄 textarea 內容變化，同步 DOM:', {
            componentContent: content,
            domContent: el.textContent
          });
          
          // 如果 DOM 內容與組件內容不一致，強制同步
          if (content !== el.textContent) {
            el.textContent = content || '';
            console.log('✅ textarea DOM 內容已同步:', content || '');
          }
          
          // 強制觸發 DOM 更新事件
          try {
            const event = new Event('input', { bubbles: true });
            el.dispatchEvent(event);
            console.log('✅ textarea DOM input 事件已觸發');
          } catch (eventError) {
            console.warn('⚠️ 觸發 DOM 事件時出錯:', eventError);
          }
        }
        
        // 強制更新組件的 innerHTML 和 textContent 屬性
        try {
          // 修復：使用從組件獲取的 content 值
          component.set('innerHTML', content || '');
          component.set('textContent', content || '');
          console.log('✅ textarea 組件 innerHTML 和 textContent 已強制更新');
        } catch (setError) {
          console.warn('⚠️ 強制更新 innerHTML/textContent 時出錯:', setError);
        }
      }
      
      // 觸發編輯器更新以確保 HTML 代碼同步
      setTimeout(() => {
        if (editor && !editor.isDestroyed && editor.refresh) {
          editor.refresh();
          console.log('✅ 編輯器已刷新，HTML 代碼已同步');
        }
      }, 50);
    } catch (error) {
      console.error('❌ component:change:content 事件處理錯誤:', error);
    }
  });

  // 添加對組件屬性變化的監聽
  editor.on('component:change:attributes', function(component) {
    // 添加安全檢查
    if (!component || !component.get || typeof component.get !== 'function') {
      console.warn('⚠️ component:change:attributes: 組件無效或已被銷毀');
      return;
    }
    
    try {
      const tagName = component.get('tagName');
      if (tagName.toLowerCase() === 'textarea') {
        console.log(' textarea 屬性變化，強制更新內容');
        
        // 強制觸發內容更新
        const content = component.get('content');
        if (content) {
          component.trigger('change:content');
          console.log('✅ textarea 內容更新事件已觸發');
        }
      }
    } catch (error) {
      console.error('❌ component:change:attributes 事件處理錯誤:', error);
    }
  });

  // 移除重複的 component:update 監聽器，避免無限循環
  // 這個監聽器會導致 textarea 內容被不斷重置
  // 已經在第一個監聽器中處理了 textarea 的內容同步
};

// 添加取消選中功能
const addDeselectFunctionality = (editor) => {
  // 監聽組件選中事件
  editor.on('component:selected', function(component) {
    // 添加安全檢查
    if (!component || !component.get || typeof component.get !== 'function') {
      console.warn('⚠️ addDeselectFunctionality component:selected: 組件無效或已被銷毀');
      return;
    }
    
    try {
      const el = component.getEl();
      
      // 檢查是否選中了 body 元素
      if (el && el.tagName === 'BODY') {
        console.log('⚠️ 檢測到 body 被選中，自動取消選中');
        editor.select(null);
      }
    } catch (error) {
      console.error('❌ addDeselectFunctionality component:selected 事件處理錯誤:', error);
      console.error('❌ 組件狀態:', {
        hasGet: !!component.get,
        getType: typeof component.get,
        component: component
      });
    }
  });
  
  // 添加 ESC 鍵取消選中功能
  const handleKeydown = function(e) {
    if (e.key === 'Escape') {
      console.log('🔍 ESC 鍵取消選中');
      editor.select(null);
      e.preventDefault();
      e.stopPropagation();
    }
  };
  
  // 添加全局鍵盤事件監聽器
  document.addEventListener('keydown', handleKeydown);
  
  // 添加點擊空白區域取消選中功能
  const handleCanvasClick = function(e) {
    // 檢查點擊的是否是空白區域（不是組件）
    const target = e.target;
    
    // 如果點擊的是 canvas 本身或 body 元素，取消選中
    if (target.tagName === 'BODY' || target.classList.contains('gjs-cv-canvas')) {
      console.log('🔍 點擊空白區域取消選中');
      editor.select(null);
      e.preventDefault();
      e.stopPropagation();
    }
  };
  
  // 監聽組件選中事件來設置 canvas 點擊監聽器
  editor.on('component:selected', function(component) {
    // 添加安全檢查
    if (!component || !component.get || typeof component.get !== 'function') {
      console.warn('⚠️ addDeselectFunctionality canvas setup component:selected: 組件無效或已被銷毀');
      return;
    }
    
    try {
      const canvas = editor.Canvas.getFrameEl();
      if (canvas) {
        canvas.addEventListener('click', handleCanvasClick);
      }
    } catch (error) {
      console.error('❌ addDeselectFunctionality canvas setup component:selected 事件處理錯誤:', error);
      console.error('❌ 組件狀態:', {
        hasGet: !!component.get,
        getType: typeof component.get,
        component: component
      });
    }
  });
  
  // 添加取消選中命令
  editor.Commands.add('deselect', {
    run: function(editor, sender, options) {
      console.log('🔍 執行取消選中命令');
      editor.select(null);
    }
  });
  
  // 返回清理函數
  return () => {
    document.removeEventListener('keydown', handleKeydown);
  };
};

/**
 * 在灰色 RTE 工具列（gjs-rte-actionbar）加入：對齊、字體、字級、字體樣式
 * 當使用者點選文字進入編輯時，工具列會顯示這些按鈕
 */
const addRteToolbarFormatActions = (editor, resources = {}) => {
  const t = resources.eformDesigner || {};
  const alignLeftTitle = t.textAlignLeft || '靠左';
  const alignCenterTitle = t.textAlignCenter || '置中';
  const alignRightTitle = t.textAlignRight || '靠右';
  const alignJustifyTitle = t.textAlignJustify || '左右對齊';
  const fontFamilyTitle = t.fontFamily || '字體';
  const fontSizeTitle = t.fontSize || '字體大小';

  editor.on('rte:enable', (view, rte) => {
    // 全域隱藏 RTE 工具列（含 iframe 內），改由 Style Manager 設定樣式
    try {
      const toolbarEl = rte && rte.actionbarEl ? rte.actionbarEl() : null;
      const doc = toolbarEl ? toolbarEl.ownerDocument : document;
      if (doc && !doc.getElementById('gjs-rte-toolbar-hide')) {
        const style = doc.createElement('style');
        style.id = 'gjs-rte-toolbar-hide';
        style.textContent = '.gjs-rte-actionbar,.gjs-rte-toolbar{display:none!important;}';
        (doc.head || doc.documentElement).appendChild(style);
      }
    } catch (e) {}
    if (!rte || !rte.addAction) return;
    const actions = rte.getActions ? rte.getActions() : [];
    if (actions.some((a) => a.name === 'alignLeft')) return;


    rte.addAction(
      {
        name: 'alignLeft',
        icon: '<span style="display:inline-block;width:20px;text-align:center;font-size:14px;line-height:1;" title="' + alignLeftTitle + '">⬅</span>',
        attributes: { title: alignLeftTitle },
        result: (rteInst) => rteInst.exec('justifyLeft'),
      },
      { sync: true }
    );
    rte.addAction(
      {
        name: 'alignCenter',
        icon: '<span style="display:inline-block;width:20px;text-align:center;font-size:14px;line-height:1;" title="' + alignCenterTitle + '">↔</span>',
        attributes: { title: alignCenterTitle },
        result: (rteInst) => rteInst.exec('justifyCenter'),
      },
      { sync: true }
    );
    rte.addAction(
      {
        name: 'alignRight',
        icon: '<span style="display:inline-block;width:20px;text-align:center;font-size:14px;line-height:1;" title="' + alignRightTitle + '">➡</span>',
        attributes: { title: alignRightTitle },
        result: (rteInst) => rteInst.exec('justifyRight'),
      },
      { sync: true }
    );
    rte.addAction(
      {
        name: 'alignJustify',
        icon: '<span style="display:inline-block;width:20px;text-align:center;font-size:14px;line-height:1;" title="' + alignJustifyTitle + '">≡</span>',
        attributes: { title: alignJustifyTitle },
        result: (rteInst) => rteInst.exec('justifyFull'),
      },
      { sync: true }
    );

    const fontOptions = [
      { v: '', l: fontFamilyTitle },
      { v: 'Arial', l: 'Arial' },
      { v: 'Microsoft JhengHei', l: '微軟正黑體' },
      { v: 'PMingLiU', l: '新細明體' },
      { v: 'Georgia', l: 'Georgia' },
      { v: 'Times New Roman', l: 'Times New Roman' },
    ];
    const fontSelectHtml =
      '<select class="gjs-field" style="width:72px;max-width:72px;height:22px;margin:0 2px;padding:0 4px;font-size:11px;background:rgba(0,0,0,.2);color:#fff;border:1px solid rgba(255,255,255,.3);border-radius:2px;box-sizing:border-box;">' +
      fontOptions.map((o) => `<option value="${o.v}">${o.l}</option>`).join('') +
      '</select>';
    rte.addAction(
      {
        name: 'fontFamily',
        icon: fontSelectHtml,
        attributes: { title: fontFamilyTitle },
        event: 'change',
        result: (rteInst, action) => {
          const sel = action.btn && action.btn.tagName === 'SELECT' ? action.btn : action.btn?.querySelector?.('select');
          if (sel && sel.value) rteInst.exec('fontName', sel.value);
        },
        update: (rteInst, action) => {
          const sel = action.btn && action.btn.tagName === 'SELECT' ? action.btn : action.btn?.querySelector?.('select');
          if (sel && rteInst.doc) {
            try {
              const val = rteInst.doc.queryCommandValue('fontName') || '';
              if (val && val !== 'false') sel.value = val;
            } catch (e) {}
          }
        },
      },
      { sync: true }
    );

    const sizeOptions = [
      { v: '12px', l: '12' },
      { v: '14px', l: '14' },
      { v: '16px', l: '16' },
      { v: '18px', l: '18' },
      { v: '20px', l: '20' },
      { v: '24px', l: '24' },
    ];
    const sizeSelectHtml =
      '<select class="gjs-field" style="width:52px;max-width:52px;height:22px;margin:0 2px;padding:0 4px;font-size:12px;background:rgba(0,0,0,.2);color:#fff;border:1px solid rgba(255,255,255,.3);border-radius:2px;box-sizing:border-box;">' +
      sizeOptions.map((o) => `<option value="${o.v}">${o.l}</option>`).join('') +
      '</select>';
    rte.addAction(
      {
        name: 'fontSize',
        icon: sizeSelectHtml,
        attributes: { title: fontSizeTitle },
        event: 'change',
        result: (rteInst, action) => {
          const sel = action.btn && action.btn.tagName === 'SELECT' ? action.btn : action.btn?.querySelector?.('select');
          if (sel && sel.value) {
            const text = rteInst.selection ? rteInst.selection() : '';
            if (text) rteInst.insertHTML(`<span style="font-size:${sel.value}">${text}</span>`, { select: true });
            else rteInst.exec('fontSize', 4);
          }
        },
        update: (rteInst, action) => {
          const sel = action.btn && action.btn.tagName === 'SELECT' ? action.btn : action.btn?.querySelector?.('select');
          if (sel && rteInst.doc) {
            try {
              const val = rteInst.doc.queryCommandValue('fontSize') || '';
              if (val && val !== 'false') sel.value = val;
            } catch (e) {}
          }
        },
      },
      { sync: true }
    );
  });
};

export default useGrapesJS; 